/**
 * DoserneLager — Warehouse shop (Faza 1).
 *
 * Workers browse materials and add quantities to a personal cart. Adding to
 * the cart RESERVES stock immediately (live availability). Reservations are
 * tied to a project and expire daily at 00:00 (Oslo) for normal users; the
 * SALG/LARS profile keeps reservations forever.
 *
 * Tabs: Materiale (shop) · Coșul meu (cart) · Coșuri active (admin).
 * Romanian UI to match the crew's working language.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Plus, Minus, Trash2, Boxes, ShoppingCart, X, Save, Package,
  Settings2, AlertTriangle, Pencil, Users, Upload, Search, Send, Download, GripVertical,
  CheckCircle2, ClipboardList, ChevronRight, QrCode, Printer, Square, CheckSquare, RotateCcw, CalendarDays, PackageCheck, Camera, MapPin, History, TrendingUp,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { QRCodeCanvas } from "qrcode.react";
import { useDoserneAuth } from "./doserneAuth";
import { useLagerT } from "./lagerI18n";
import DoserneShell from "./DoserneShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Standard color palette for materials (dropdown in the catalog editor).
const COLORS = ["Zincat", "Galben", "Negru", "Albastru", "Gri", "Roșu", "Verde", "Alb", "Portocaliu"];
const SYSTEMS = ["Haki", "Ubix", "Layher"];
const COLOR_HEX = {
  Zincat: "#9ca3af", Galben: "#facc15", Negru: "#1f2937", Albastru: "#3b82f6",
  Gri: "#6b7280", "Roșu": "#ef4444", Verde: "#22c55e", Alb: "#f8fafc", Portocaliu: "#f97316",
};

// -----------------------------------------------------------------
// Active-project bar + project picker (one project per "list")
// -----------------------------------------------------------------
function ActiveProjectBar({ activeProject, onPick, testId }) {
  const t = useLagerT();
  return (
    <button
      onClick={onPick}
      className={`w-full mb-4 flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition ${activeProject ? "border-teal-300 bg-teal-50 hover:bg-teal-100" : "border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100"}`}
      data-testid={testId || "lager-active-project"}
    >
      <ClipboardList className={`w-4 h-4 flex-shrink-0 ${activeProject ? "text-teal-700" : "text-slate-400"}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-black text-slate-400">{t("active_list")}</div>
        <div className={`text-sm font-black truncate ${activeProject ? "text-teal-800" : "text-slate-500"}`}>{activeProject ? activeProject.name : t("pick_project")}</div>
      </div>
      <span className="text-[11px] font-black text-teal-700 flex items-center gap-1 flex-shrink-0"><Plus className="w-3.5 h-3.5" /> {t("new_list")}</span>
    </button>
  );
}

function ProjectPickerModal({ projects, onClose, onPick }) {
  const t = useLagerT();
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const filtered = ql ? projects.filter((p) => `${p.name} ${p.address} ${p.number}`.toLowerCase().includes(ql)) : projects;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="lager-project-picker">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-black text-slate-900">{t("new_list")}</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-3 border-b border-slate-100">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 focus:border-teal-500 focus:outline-none" data-testid="lager-project-picker-search" />
          </div>
        </div>
        <div className="overflow-y-auto">
          {filtered.map((p) => (
            <button key={p.id} onClick={() => { onPick({ id: p.id, name: p.name || p.address || p.id }); onClose(); }} className="w-full text-left px-5 py-3 hover:bg-teal-50 border-b border-slate-100 flex items-center gap-2" data-testid={`lager-project-opt-${p.id}`}>
              <ClipboardList className="w-4 h-4 text-teal-600 flex-shrink-0" />
              <span className="flex-1 min-w-0"><span className="text-sm font-black text-slate-800 block truncate">{p.name || p.address}</span>{p.number && <span className="text-[11px] text-slate-400">{p.number}</span>}</span>
            </button>
          ))}
          {filtered.length === 0 && <div className="p-6 text-center text-sm text-slate-400 italic">—</div>}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// ADD-TO-CART sheet (active project + job + quantity)
// -----------------------------------------------------------------
function AddSheet({ item, activeProject, onClose, onAdd, authHeader, projects, setActiveProject }) {
  const t = useLagerT();
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const max = item.available;

  useEffect(() => {
    if (!activeProject?.id) { setJobs([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/projects/${activeProject.id}/jobs`, authHeader());
        if (!cancelled) setJobs(data.jobs || data || []);
      } catch { if (!cancelled) setJobs([]); }
    })();
    return () => { cancelled = true; };
  }, [activeProject, authHeader]);

  const submit = async () => {
    if (quantity < 1 || quantity > max) return;
    setBusy(true);
    const job = jobs.find((j) => j.id === jobId);
    const ok = await onAdd({
      item_id: item.id,
      quantity: Math.min(max, Math.max(1, Number(quantity) || 1)),
      project_id: activeProject?.id || "",
      project_name: activeProject?.name || "",
      job_id: jobId,
      job_name: job ? (job.title || job.name || "") : "",
      note,
    });
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="lager-add-sheet">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-teal-700 font-black">{t("add_sheet_title")}</div>
            <h2 className="text-xl font-black text-slate-900">{item.name}</h2>
            <div className="text-xs text-slate-500 mt-0.5">{t("avail_line", { a: item.available, b: item.quantity, unit: item.unit })}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          <button
            type="button"
            onClick={() => setShowPicker(true)}
            className={`w-full rounded-lg border px-3 py-2 flex items-center gap-2 text-left transition ${activeProject ? "bg-teal-50 border-teal-200 hover:bg-teal-100" : "bg-amber-50 border-amber-300 hover:bg-amber-100"}`}
            data-testid="lager-add-active-project"
          >
            <ClipboardList className={`w-4 h-4 flex-shrink-0 ${activeProject ? "text-teal-700" : "text-amber-600"}`} />
            <span className={`text-sm font-black truncate flex-1 ${activeProject ? "text-teal-800" : "text-amber-700"}`}>{activeProject ? activeProject.name : t("pick_project")}</span>
            <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
          </button>
          {jobs.length > 1 && (
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1.5">{t("job")}</label>
              <select value={jobId} onChange={(e) => setJobId(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900" data-testid="lager-add-job">
                <option value="">{t("no_job")}</option>
                {jobs.map((j) => <option key={j.id} value={j.id}>{j.title || j.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1.5">{t("qty_max", { n: max })}</label>
            <div className="flex items-center gap-2">
              <button onClick={() => setQuantity((q) => Math.max(1, Number(q) - 1))} className="w-10 h-10 rounded-lg border border-slate-300 hover:bg-slate-100 flex items-center justify-center" data-testid="lager-add-dec"><Minus className="w-4 h-4 text-slate-600" /></button>
              <input type="number" min={1} max={max} value={quantity} onChange={(e) => setQuantity(e.target.value)} className="w-20 px-2 py-2 border border-slate-300 rounded-lg text-sm text-center text-slate-900" data-testid="lager-add-qty" />
              <button onClick={() => setQuantity((q) => Math.min(max, Number(q) + 1))} className="w-10 h-10 rounded-lg border border-slate-300 hover:bg-slate-100 flex items-center justify-center" data-testid="lager-add-inc"><Plus className="w-4 h-4 text-slate-600" /></button>
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1.5">{t("note_opt")}</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-add-note" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100">{t("cancel")}</button>
          <button onClick={submit} disabled={busy || max < 1 || quantity < 1 || quantity > max} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-sm font-black" style={{ color: "#fff" }} data-testid="lager-add-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />} {t("reserve")}
          </button>
        </div>
      </div>
      {showPicker && <ProjectPickerModal projects={projects || []} onClose={() => setShowPicker(false)} onPick={setActiveProject} />}
    </div>
  );
}

// -----------------------------------------------------------------
// SHOP tab
// -----------------------------------------------------------------
function StockCell({ item, isAdmin, onAdjust }) {
  const t = useLagerT();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(item.quantity);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const q = Math.max(0, Number(val) || 0);
    if (q === item.quantity) { setEditing(false); return; }
    setBusy(true);
    const ok = await onAdjust(item.id, q);
    setBusy(false);
    if (ok) setEditing(false);
  };
  if (!isAdmin) return <span className="font-bold text-slate-800">{item.quantity} {item.unit}</span>;
  if (!editing) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setVal(item.quantity); setEditing(true); }}
        className="inline-flex items-center gap-1 font-bold text-slate-800 px-2 py-0.5 rounded hover:bg-cyan-100 border border-transparent hover:border-cyan-300"
        data-testid={`lager-stock-edit-${item.id}`}
        title={t("e.stock")}
      >
        {item.quantity} {item.unit} <Pencil className="w-3 h-3 text-slate-400" />
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input
        type="number" min={0} value={val} autoFocus
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        className="w-16 px-2 py-1 border border-cyan-400 rounded text-sm text-center text-slate-900"
        data-testid={`lager-stock-input-${item.id}`}
      />
      <button onClick={save} disabled={busy} className="w-7 h-7 rounded bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center" data-testid={`lager-stock-save-${item.id}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}</button>
      <button onClick={() => setEditing(false)} className="w-7 h-7 rounded border border-slate-300 text-slate-500 flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
    </span>
  );
}

function DashboardPanels({ authHeader }) {
  const t = useLagerT();
  const [recent, setRecent] = useState([]);
  const [packing, setPacking] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rl, rq] = await Promise.all([
          axios.get(`${API}/doserne/lager/recent-lists`, authHeader()),
          axios.get(`${API}/doserne/lager/requests`, authHeader()),
        ]);
        if (!cancelled) {
          setRecent(rl.data.lists || []);
          setPacking((rq.data.requests || []).filter((r) => r.status === "preparing" || r.status === "ready"));
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [authHeader]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5" data-testid="lager-dashboard">
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-teal-50 border-b border-teal-100">
          <ClipboardList className="w-4 h-4 text-teal-700" />
          <div className="text-xs uppercase tracking-widest font-black text-teal-800">{t("dash.recent")}</div>
          <span className="ml-auto text-[10px] font-mono text-slate-400">{recent.length}</span>
        </div>
        {recent.length === 0 ? (
          <div className="p-5 text-center text-xs text-slate-400 italic">{t("dash.empty")}</div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {recent.map((l) => (
              <li key={l.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`dash-list-${l.id}`}>
                <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0"><ClipboardList className="w-4 h-4 text-teal-600" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-black text-slate-900 truncate">{l.title || "—"}</div>
                  <div className="text-[11px] text-slate-500 truncate">{l.project_name || t("dash.no_project")}{l.created_by_name ? ` · ${l.created_by_name}` : ""}</div>
                </div>
                {l.linked > 0 && <span className="text-[10px] font-black text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-1.5 py-0.5 flex-shrink-0">{l.linked} Lager</span>}
                <span className="text-[11px] font-black text-slate-500 tabular-nums flex-shrink-0">{l.prepared}/{l.total}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-teal-50 border-b border-teal-100">
          <Package className="w-4 h-4 text-teal-700" />
          <div className="text-xs uppercase tracking-widest font-black text-teal-800">{t("dash.packing")}</div>
          <span className="ml-auto text-[10px] font-mono text-slate-400">{packing.length}</span>
        </div>
        {packing.length === 0 ? (
          <div className="p-5 text-center text-xs text-slate-400 italic">{t("dash.packing_empty")}</div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {packing.map((r) => {
              const done = (r.lines || []).filter((l) => l.checked).length;
              const tot = (r.lines || []).length;
              return (
                <li key={r.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`dash-packing-${r.id}`}>
                  <span className={`text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded ${r.status === "ready" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>{t(`s.${r.status}`)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-black text-slate-900 font-mono truncate">{r.code}{r.list_name ? ` · ${r.list_name}` : ""}</div>
                    <div className="text-[11px] text-slate-500 truncate">{r.project_name || t("dash.no_project")}</div>
                  </div>
                  <span className="text-[11px] font-black text-slate-500 tabular-nums flex-shrink-0">{done}/{tot}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function DetailImageUploader({ item, isAdmin, authHeader, onReload }) {
  const t = useLagerT();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [focused, setFocused] = useState(false);

  const doUpload = async (file) => {
    if (!file || !file.type?.startsWith("image/")) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/doserne/lager/admin/upload-image`, form, {
        ...authHeader(), headers: { ...(authHeader().headers || {}), "Content-Type": "multipart/form-data" },
      });
      if (data.url) {
        await axios.patch(`${API}/doserne/lager/catalog/${item.id}/image`, { image_url: data.url }, authHeader());
        toast.success(t("img.saved"));
        onReload?.();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
    setBusy(false);
  };
  const onPaste = (e) => {
    const f = Array.from(e.clipboardData?.items || [])
      .filter((i) => i.kind === "file" && (i.type || "").startsWith("image/"))
      .map((i) => i.getAsFile())[0];
    if (f) { e.preventDefault(); doUpload(f); }
  };

  if (!isAdmin) {
    return (
      <div className="w-full sm:w-40 h-40 rounded-xl bg-white border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
        {item.image_url ? <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" /> : <Package className="w-10 h-10 text-slate-300" />}
      </div>
    );
  }
  return (
    <div
      tabIndex={0}
      onPaste={onPaste}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onClick={(e) => e.stopPropagation()}
      className={`relative w-full sm:w-40 h-40 rounded-xl bg-white border-2 ${focused ? "border-cyan-500 ring-2 ring-cyan-200" : "border-dashed border-slate-300"} flex items-center justify-center overflow-hidden flex-shrink-0 outline-none`}
      data-testid={`lager-detail-img-${item.id}`}
    >
      {item.image_url ? <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" /> : <Package className="w-10 h-10 text-slate-300" />}
      {busy && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>}
      <div className="absolute inset-x-0 bottom-0 bg-slate-900/75 text-white text-[10px] font-bold px-2 py-1 flex items-center justify-between gap-1">
        <span className="truncate">{focused ? t("img.paste_now") : t("img.hint")}</span>
        <button onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }} className="underline flex-shrink-0" data-testid={`lager-detail-img-upload-${item.id}`}>{t("img.upload")}</button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; doUpload(f); }} />
    </div>
  );
}

// -----------------------------------------------------------------
// LOCATIONS panel — surplus stock available per project (+ transfer).
// Warehouse stock stays the item's `quantity`; this shows the extra
// material sitting on projects, available to be picked up / relocated.
// -----------------------------------------------------------------
function LocationsPanel({ item, projects, authHeader, canTransfer, onChanged }) {
  const t = useLagerT();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [projId, setProjId] = useState("");
  const [qty, setQty] = useState("");
  const [moveTo, setMoveTo] = useState({}); // { [project_id]: destValue }
  const [moveQty, setMoveQty] = useState({});

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/doserne/lager/items/${item.id}/locations`, authHeader()); setData(data); }
    catch { /* ignore */ }
  }, [item.id, authHeader]);
  useEffect(() => { load(); }, [load]);

  const postSurplus = async () => {
    if (!projId || !(Number(qty) > 0)) return;
    const proj = projects.find((p) => p.id === projId);
    setBusy(true);
    try {
      await axios.post(`${API}/doserne/lager/items/${item.id}/project-stock`,
        { project_id: projId, project_name: proj?.name || "", quantity: Number(qty), mode: "add" }, authHeader());
      setQty(""); await load(); onChanged?.(); toast.success(t("loc_posted"));
    } catch { toast.error("Feil"); } finally { setBusy(false); }
  };
  const doTransfer = async (fromId) => {
    const q = Number(moveQty[fromId]) || 0;
    const dest = moveTo[fromId] || "lager";
    if (q <= 0) return;
    setBusy(true);
    try {
      await axios.post(`${API}/doserne/lager/items/${item.id}/transfer`,
        { from_project_id: fromId, to_project_id: dest, quantity: q }, authHeader());
      await load(); onChanged?.(); toast.success(t("loc_moved"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Feil"); } finally { setBusy(false); }
  };

  if (!data) return null;
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3" data-testid={`lager-locations-${item.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <MapPin className="w-4 h-4 text-cyan-600" />
        <span className="text-sm font-black text-slate-800">{t("loc_title")}</span>
        <span className="ml-auto text-xs font-black text-slate-500">{t("loc_total")}: {data.total_all} {data.unit}</span>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg bg-emerald-50">
          <Package className="w-3.5 h-3.5 text-emerald-600" />
          <span className="font-bold text-emerald-800 flex-1">Lager</span>
          <span className="font-black text-emerald-700">{data.lager_available} {data.unit}</span>
        </div>
        {data.locations.map((l) => (
          <div key={l.project_id} className="px-2 py-1.5 rounded-lg bg-slate-50" data-testid={`lager-loc-row-${item.id}-${l.project_id}`}>
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="w-3.5 h-3.5 text-cyan-500" />
              <span className="font-bold text-slate-700 flex-1 truncate">{l.project_name || l.project_id}</span>
              <span className="font-black text-cyan-700">{l.quantity} {data.unit}</span>
            </div>
            {canTransfer && (
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                <input type="number" min={1} max={l.quantity} placeholder={t("loc_qty")} value={moveQty[l.project_id] || ""}
                  onChange={(e) => setMoveQty((m) => ({ ...m, [l.project_id]: e.target.value }))}
                  className="w-16 px-2 py-1 border border-slate-300 rounded text-xs text-slate-900" data-testid={`lager-move-qty-${item.id}-${l.project_id}`} />
                <select value={moveTo[l.project_id] || "lager"} onChange={(e) => setMoveTo((m) => ({ ...m, [l.project_id]: e.target.value }))}
                  className="px-2 py-1 border border-slate-300 rounded text-xs text-slate-700 bg-white max-w-[160px]" data-testid={`lager-move-dest-${item.id}-${l.project_id}`}>
                  <option value="lager">→ Lager</option>
                  {projects.filter((p) => p.id !== l.project_id).map((p) => <option key={p.id} value={p.id}>→ {p.name}</option>)}
                </select>
                <button onClick={() => doTransfer(l.project_id)} disabled={busy} className="px-2.5 py-1 rounded bg-slate-900 text-white text-xs font-black disabled:opacity-50" data-testid={`lager-move-btn-${item.id}-${l.project_id}`}>{t("loc_move")}</button>
              </div>
            )}
          </div>
        ))}
        {data.locations.length === 0 && <div className="text-xs text-slate-400 italic px-2 py-1">{t("loc_none")}</div>}
      </div>
      <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-slate-100 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-black w-full mb-0.5">{t("loc_add")}</span>
        <select value={projId} onChange={(e) => setProjId(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs text-slate-700 bg-white flex-1 min-w-[140px]" data-testid={`lager-surplus-proj-${item.id}`}>
          <option value="">{t("loc_pick_project")}</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <input type="number" min={1} placeholder={t("loc_qty")} value={qty} onChange={(e) => setQty(e.target.value)} className="w-20 px-2 py-1.5 border border-slate-300 rounded-lg text-xs text-slate-900" data-testid={`lager-surplus-qty-${item.id}`} />
        <button onClick={postSurplus} disabled={busy || !projId || !(Number(qty) > 0)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-xs font-black" data-testid={`lager-surplus-add-${item.id}`}><Plus className="w-3.5 h-3.5" /> {t("loc_add_btn")}</button>
      </div>
    </div>
  );
}


function ShopTab({ items, categories, systemsOrder = [], projects, onAdd, isAdmin, isDepot, onAdjustStock, authHeader, activeProject, setActiveProject, onReload }) {
  const [activeSystem, setActiveSystem] = useState("__all__");
  const [activeCat, setActiveCat] = useState("__all__");
  const [query, setQuery] = useState("");
  const [sheetItem, setSheetItem] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const t = useLagerT();

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/doserne/lager/export`, { ...authHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lager_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export gata");
    } catch { toast.error("Export eșuat"); }
    finally { setExporting(false); }
  };

  const importCatalog = async () => {
    if (!window.confirm("Importă catalogul Haki (produsele lipsă) în acest depozit? Produsele existente nu sunt afectate.")) return;
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/doserne/lager/import-seed`, {}, authHeader());
      toast.success(`Import gata: ${data.inserted} produse adăugate${data.skipped ? `, ${data.skipped} existau deja` : ""}`);
      onReload?.();
    } catch { toast.error("Import eșuat"); }
    finally { setImporting(false); }
  };

  const replaceCatalog = async () => {
    if (!window.confirm("⚠️ ATENȚIE: Aceasta ȘTERGE TOT catalogul Lager actual (toate produsele, rezervările și stocul pe proiecte) și îl înlocuiește complet cu noul catalog HAKI din PDF.\n\nContinui?")) return;
    if (!window.confirm("Ești 100% sigur? Această acțiune este DEFINITIVĂ și nu poate fi anulată.")) return;
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/doserne/lager/import-seed?replace=true`, {}, authHeader());
      toast.success(`Catalog înlocuit: ${data.deleted} șterse, ${data.inserted} produse HAKI adăugate`);
      onReload?.();
    } catch { toast.error("Înlocuirea a eșuat"); }
    finally { setImporting(false); }
  };

  // Two-level taxonomy: system (Haki/Ubix/Layher…) → sub-category (Universal/Frames…).
  // Order follows the admin's custom taxonomy order when available, else A–Z.
  const systems = React.useMemo(() => {
    const present = Array.from(new Set(items.map((i) => (i.system || "").trim()).filter(Boolean)));
    const idx = {}; systemsOrder.forEach((n, i) => { idx[n] = i; });
    return present.sort((a, b) => (idx[a] ?? 9999) - (idx[b] ?? 9999) || a.localeCompare(b));
  }, [items, systemsOrder]);
  const subcats = React.useMemo(() => {
    const pool = activeSystem === "__all__" ? items : items.filter((i) => (i.system || "") === activeSystem);
    const present = Array.from(new Set(pool.map((i) => (i.category || "").trim()).filter(Boolean)));
    const idx = {}; (categories || []).forEach((n, i) => { idx[n] = i; });
    return present.sort((a, b) => (idx[a] ?? 9999) - (idx[b] ?? 9999) || a.localeCompare(b));
  }, [items, activeSystem, categories]);
  useEffect(() => { setActiveCat("__all__"); }, [activeSystem]);

  const q = query.trim().toLowerCase();
  let pool = items;
  if (activeSystem !== "__all__") pool = pool.filter((i) => (i.system || "") === activeSystem);
  if (activeCat !== "__all__") pool = pool.filter((i) => (i.category || "") === activeCat);
  const visible = q
    ? pool.filter((i) => `${i.name} ${i.name_no || ""} ${i.name_en || ""} ${i.sku} ${i.art_no || ""} ${i.tripletex_code || ""} ${i.category} ${i.system}`.toLowerCase().includes(q))
    : pool;

  return (
    <div data-testid="lager-shop-tab">
      <DashboardPanels authHeader={authHeader} />
      {isAdmin && (
        <div className="mb-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={importCatalog}
            disabled={importing}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-xs font-black"
            data-testid="lager-import-btn"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Importă catalog
          </button>
          <button
            type="button"
            onClick={replaceCatalog}
            disabled={importing}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-black"
            data-testid="lager-replace-btn"
            title="Șterge tot catalogul și importă noul catalog HAKI"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Șterge tot & Importă HAKI
          </button>
          <button
            type="button"
            onClick={exportExcel}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-black"
            data-testid="lager-export-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export Excel
          </button>
        </div>
      )}
      <ActiveProjectBar activeProject={activeProject} onPick={() => setShowPicker(true)} />
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("search")}
          className="w-full pl-9 pr-9 py-2.5 border border-slate-300 rounded-xl text-sm text-slate-900 focus:border-cyan-500 focus:outline-none"
          data-testid="lager-search"
        />
        {query && (
          <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="lager-search-clear"><X className="w-4 h-4" /></button>
        )}
      </div>
      {systems.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar" data-testid="lager-system-chips">
          {[{ key: "__all__", label: t("all_systems") }, ...systems.map((s) => ({ key: s, label: s }))].map((chip) => (
            <button
              key={chip.key}
              onClick={() => setActiveSystem(chip.key)}
              className={`flex-shrink-0 text-xs font-black px-4 py-1.5 rounded-full border-2 transition whitespace-nowrap ${activeSystem === chip.key ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-slate-700 border-slate-200 hover:border-cyan-400"}`}
              data-testid={`lager-system-${chip.key}`}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}
      {subcats.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-1 -mx-1 px-1 no-scrollbar" data-testid="lager-category-chips">
          {[{ key: "__all__", label: t("all") }, ...subcats.map((c) => ({ key: c, label: c }))].map((chip) => (
            <button
              key={chip.key}
              onClick={() => setActiveCat(chip.key)}
              className={`flex-shrink-0 text-xs font-bold px-3.5 py-1.5 rounded-full border transition whitespace-nowrap ${activeCat === chip.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
              data-testid={`lager-chip-${chip.key}`}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}

      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="lager-grid">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold border-b border-slate-200">
              <tr>
                <th className="w-14 px-2 py-2.5"></th>
                <th className="text-left px-3 py-2.5">{t("h.name")}</th>
                <th className="text-left px-3 py-2.5">{t("h.material")}</th>
                <th className="text-left px-3 py-2.5">{t("h.color")}</th>
                <th className="text-left px-3 py-2.5">{t("h.system")}</th>
                <th className="text-center px-3 py-2.5">{t("h.stock")}</th>
                <th className="text-center px-3 py-2.5">{t("h.avail")}</th>
                <th className="w-12 px-2 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => {
                const soldOut = item.available < 1;
                const open = expandedId === item.id;
                return (
                  <React.Fragment key={item.id}>
                    <tr
                      onClick={() => setExpandedId(open ? null : item.id)}
                      className={`border-b border-slate-100 cursor-pointer transition ${open ? "bg-cyan-50/60" : "hover:bg-slate-50"}`}
                      data-testid={`lager-row-${item.id}`}
                    >
                      <td className="px-2 py-2">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
                          {item.image_url ? <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-slate-400" />}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-black text-slate-900 flex items-center gap-1.5">
                          <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
                          {item.name}
                        </div>
                        {(item.art_no || item.sku) && <div className="text-[10px] text-slate-400 font-mono ml-5">{item.art_no || item.sku}</div>}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{item.material || "—"}</td>
                      <td className="px-3 py-2">
                        {item.color ? (
                          <span className="inline-flex items-center gap-1.5 text-slate-700">
                            <span className="w-3 h-3 rounded-full border border-slate-300" style={{ backgroundColor: COLOR_HEX[item.color] || "#cbd5e1" }} />
                            {item.color}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{item.system || "—"}</td>
                      <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <StockCell item={item} isAdmin={isAdmin} onAdjust={onAdjustStock} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-xs font-black px-2 py-0.5 rounded ${soldOut ? "bg-red-100 text-red-700" : item.low_stock ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`lager-avail-${item.id}`}>
                          {item.available}
                        </span>
                        {item.project_stock_total > 0 && (
                          <span className="block text-[10px] font-black text-cyan-600 mt-0.5" data-testid={`lager-projstock-${item.id}`}>+{item.project_stock_total} 📍 · Σ{item.total_all}</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); if (!soldOut) setSheetItem(item); }}
                          disabled={soldOut}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition ${soldOut ? "bg-slate-100 text-slate-300" : "bg-cyan-50 hover:bg-cyan-600 text-cyan-700 hover:text-white"}`}
                          data-testid={`lager-add-btn-${item.id}`}
                          title={t("add_to_cart")}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-cyan-50/30 border-b border-slate-100" data-testid={`lager-row-detail-${item.id}`}>
                        <td colSpan={8} className="px-4 py-4">
                          <div className="flex gap-4 flex-col sm:flex-row">
                            <DetailImageUploader item={item} isAdmin={isAdmin} authHeader={authHeader} onReload={onReload} />
                            <div className="flex-1 min-w-0">
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                                {[
                                  [t("l.artno"), item.art_no || item.sku || "—"],
                                  [t("l.category"), item.category || "—"],
                                  [t("h.system"), item.system || "—"],
                                  [t("h.material"), item.material || "—"],
                                  [t("h.color"), item.color || "—"],
                                  [t("l.size"), item.size || "—"],
                                  [t("l.weight"), item.weight_kg ? `${item.weight_kg} kg` : "—"],
                                  [t("l.diameter"), item.diameter_mm ? `${item.diameter_mm} mm` : "—"],
                                  [t("l.unit"), item.unit],
                                  [t("l.instock"), `${item.quantity} ${item.unit}`],
                                  [t("l.reserved"), `${item.reserved} ${item.unit}`],
                                  [t("h.avail"), `${item.available} ${item.unit}`],
                                ].map(([k, v]) => (
                                  <div key={k}>
                                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black">{k}</div>
                                    <div className="text-slate-800 font-semibold">{v}</div>
                                  </div>
                                ))}
                              </div>
                              {item.notes && <div className="mt-3 text-sm text-slate-600"><span className="text-[10px] uppercase tracking-widest text-slate-400 font-black block">{t("l.details")}</span>{item.notes}</div>}
                              <LocationsPanel item={item} projects={projects} authHeader={authHeader} canTransfer={isAdmin || isDepot} onChanged={onReload} />
                              <button
                                onClick={(e) => { e.stopPropagation(); if (!soldOut) setSheetItem(item); }}
                                disabled={soldOut}
                                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-sm font-black"
                                style={{ color: "#fff" }}
                                data-testid={`lager-detail-add-${item.id}`}
                              >
                                <ShoppingCart className="w-4 h-4" /> {soldOut ? t("sold_out") : t("add_to_cart")}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {visible.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-400 italic">{t("no_materials")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {sheetItem && (
        <AddSheet item={sheetItem} activeProject={activeProject} authHeader={authHeader} projects={projects} setActiveProject={setActiveProject} onClose={() => setSheetItem(null)} onAdd={onAdd} />
      )}
      {showPicker && <ProjectPickerModal projects={projects} onClose={() => setShowPicker(false)} onPick={setActiveProject} />}
    </div>
  );
}

// -----------------------------------------------------------------
// CART tab (my live reservations)
// -----------------------------------------------------------------
function CartTab({ cart, isSalg, onQty, onRemove, onConvert, onAddMore, activeProject, setActiveProject, projects }) {
  const t = useLagerT();
  const [busyId, setBusyId] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [dates, setDates] = useState({}); // { [pid]: "YYYY-MM-DD" } — required before send
  const todayIso = new Date().toISOString().slice(0, 10);

  // Group cart lines into one "list" per project, then by job inside each.
  const groups = {};
  for (const l of cart) {
    const pid = l.project_id || "";
    if (!groups[pid]) groups[pid] = { project_id: pid, project_name: l.project_name || "", lines: [] };
    groups[pid].lines.push(l);
  }
  const groupList = Object.values(groups).sort((a, b) => (a.project_name || "\uffff").localeCompare(b.project_name || "\uffff"));

  const sendGroup = async (g) => {
    const key = g.project_id || "__none__";
    const date = dates[key];
    if (!date) { toast.error(t("delivery_pick")); return; }
    setBusyId(key);
    await onConvert(g.project_id, g.project_name, date);
    setBusyId(null);
  };

  if (cart.length === 0) {
    return (
      <div data-testid="lager-cart-tab">
        <ActiveProjectBar activeProject={activeProject} onPick={() => setShowPicker(true)} />
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="lager-cart-empty">
          <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-sm text-slate-500 italic">{t("cart_empty")}</div>
        </div>
        {showPicker && <ProjectPickerModal projects={projects} onClose={() => setShowPicker(false)} onPick={setActiveProject} />}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="lager-cart-tab">
      <ActiveProjectBar activeProject={activeProject} onPick={() => setShowPicker(true)} />
      <div className={`rounded-xl border p-3 text-xs font-semibold ${isSalg ? "border-teal-200 bg-teal-50 text-teal-800" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
        {isSalg ? t("salg_yes") : t("salg_no")}
      </div>

      {groupList.map((g) => {
        const diameters = [...new Set(g.lines.map((l) => Number(l.diameter_mm) || 0).filter((d) => d > 0))];
        const incompatible = diameters.length > 1;
        const kg = g.lines.reduce((s, l) => s + (Number(l.weight_kg) || 0) * (Number(l.quantity) || 0), 0);
        // Sub-group by job.
        const jobs = {};
        for (const l of g.lines) {
          const jk = l.job_name || "";
          if (!jobs[jk]) jobs[jk] = [];
          jobs[jk].push(l);
        }
        const jobKeys = Object.keys(jobs).sort((a, b) => (a || "\uffff").localeCompare(b || "\uffff"));
        return (
          <div key={g.project_id || "__none__"} className="rounded-2xl border-2 border-slate-200 bg-white overflow-hidden" data-testid={`lager-cart-group-${g.project_id || "none"}`}>
            <div className="flex items-center gap-2 px-4 py-2.5 bg-teal-50 border-b border-teal-100">
              <ClipboardList className="w-4 h-4 text-teal-700" />
              <div className="text-sm font-black text-teal-800 truncate flex-1">{g.project_name || t("no_project")}</div>
              <span className="text-[11px] font-bold text-slate-500">{kg.toFixed(1)} kg</span>
            </div>
            {incompatible && (
              <div className="mx-3 mt-3 rounded-lg border border-red-300 bg-red-50 p-2 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-red-700 font-bold">{t("incompatible", { d: diameters.map((d) => `${d}mm`).join(" / ") })}</div>
              </div>
            )}
            <div className="p-3 space-y-2">
              {jobKeys.map((jk) => (
                <div key={jk || "__nojob__"}>
                  {jobKeys.length > 1 || jk ? (
                    <div className="text-[10px] uppercase tracking-widest font-black text-slate-400 px-1 mb-1">{jk || t("no_job")}</div>
                  ) : null}
                  {jobs[jk].map((l) => (
                    <div key={l.id} className="flex items-center gap-3 py-1.5" data-testid={`lager-cart-line-${l.id}`}>
                      <div className="w-11 h-11 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {l.image_url ? <img src={l.image_url} alt={l.item_name} className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-slate-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-black text-slate-900 truncate">{l.item_name}</div>
                        {l.note && <div className="text-[11px] text-slate-500 truncate">„{l.note}"</div>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => onQty(l, l.quantity - 1)} disabled={l.quantity <= 1} className="w-7 h-7 rounded-lg border border-slate-300 hover:bg-slate-100 disabled:opacity-40 flex items-center justify-center" data-testid={`lager-cart-dec-${l.id}`}><Minus className="w-3 h-3 text-slate-600" /></button>
                        <span className="w-8 text-center text-sm font-black text-slate-900">{l.quantity}</span>
                        <button onClick={() => onQty(l, l.quantity + 1)} className="w-7 h-7 rounded-lg border border-slate-300 hover:bg-slate-100 flex items-center justify-center" data-testid={`lager-cart-inc-${l.id}`}><Plus className="w-3 h-3 text-slate-600" /></button>
                        <button onClick={() => onRemove(l)} className="ml-1 w-7 h-7 rounded-lg hover:bg-red-50 border border-red-200 flex items-center justify-center text-red-500" data-testid={`lager-cart-remove-${l.id}`}><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              <div className="mt-2 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <CalendarDays className="w-4 h-4 text-teal-700 flex-shrink-0" />
                <label className="text-[11px] font-black uppercase tracking-wide text-slate-500">{t("delivery_when")}</label>
                <input
                  type="date"
                  min={todayIso}
                  value={dates[g.project_id || "__none__"] || ""}
                  onChange={(e) => setDates((d) => ({ ...d, [g.project_id || "__none__"]: e.target.value }))}
                  className="ml-auto rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid={`lager-cart-date-${g.project_id || "none"}`}
                />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={() => onAddMore(g.project_id, g.project_name)}
                  className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-teal-300 bg-teal-50 hover:bg-teal-100 text-sm font-black text-teal-800"
                  data-testid={`lager-cart-addmore-${g.project_id || "none"}`}
                >
                  <Plus className="w-4 h-4" /> {t("add_more")}
                </button>
                <button
                  onClick={() => sendGroup(g)}
                  disabled={busyId !== null || !dates[g.project_id || "__none__"]}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-sm font-black"
                  style={{ color: "#fff" }}
                  data-testid={`lager-cart-send-${g.project_id || "none"}`}
                >
                  {busyId === (g.project_id || "__none__") ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("send_project")}
                </button>
              </div>
            </div>
          </div>
        );
      })}
      {showPicker && <ProjectPickerModal projects={projects} onClose={() => setShowPicker(false)} onPick={setActiveProject} />}
    </div>
  );
}

// -----------------------------------------------------------------
// REQUESTS tab (OUT/IN/RETURN with state machine)
// -----------------------------------------------------------------
const REQ_TYPE_META = {
  out: { key: "t.out", cls: "bg-rose-100 text-rose-700" },
  in: { key: "t.in", cls: "bg-emerald-100 text-emerald-700" },
  return: { key: "t.return", cls: "bg-sky-100 text-sky-700" },
};
const REQ_STATUS_META = {
  draft: { key: "s.draft", cls: "bg-slate-100 text-slate-600" },
  preparing: { key: "s.preparing", cls: "bg-amber-100 text-amber-700" },
  ready: { key: "s.ready", cls: "bg-indigo-100 text-indigo-700" },
  done: { key: "s.done", cls: "bg-emerald-600 text-white" },
  cancelled: { key: "s.cancelled", cls: "bg-slate-200 text-slate-500" },
};
const NEXT_STATUS = {
  draft: [["preparing", "n.preparing"], ["cancelled", "n.cancel"]],
  preparing: [["ready", "n.ready"], ["cancelled", "n.cancel"]],
  ready: [["done", "n.done"], ["preparing", "n.back"]],
  done: [],
  cancelled: [],
};

function CreateRequestModal({ items, onClose, authHeader, onCreated }) {
  const t = useLagerT();
  const [type, setType] = useState("in");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([{ item_id: "", quantity: 1 }]);
  const [busy, setBusy] = useState(false);
  const setLine = (idx, patch) => setLines((cur) => cur.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const submit = async () => {
    const clean = lines.filter((l) => l.item_id && Number(l.quantity) > 0);
    if (!clean.length) { toast.error(t("cr.need")); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/doserne/lager/requests`, { type, note, lines: clean }, authHeader());
      toast.success(t("cr.created"));
      onCreated(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="lager-create-request">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-black text-slate-900">{t("cr.title")}</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1.5">{t("cr.type")}</label>
            <div className="flex gap-2">
              {[["in", t("cr.in")], ["return", t("cr.return")]].map(([v, lbl]) => (
                <button key={v} onClick={() => setType(v)} className={`flex-1 px-3 py-2 rounded-lg border text-sm font-black ${type === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`} data-testid={`lager-req-type-${v}`}>{lbl}</button>
              ))}
            </div>
          </div>
          {lines.map((l, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <select value={l.item_id} onChange={(e) => setLine(idx, { item_id: e.target.value })} className="flex-1 px-2 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900" data-testid={`lager-req-line-item-${idx}`}>
                <option value="">{t("cr.pick")}</option>
                {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
              <input type="number" min={1} value={l.quantity} onChange={(e) => setLine(idx, { quantity: Number(e.target.value) || 1 })} className="w-16 px-2 py-2 border border-slate-300 rounded-lg text-sm text-center text-slate-900" data-testid={`lager-req-line-qty-${idx}`} />
              <button onClick={() => setLines((cur) => cur.length <= 1 ? cur : cur.filter((_, i) => i !== idx))} className="w-8 h-8 rounded-lg hover:bg-red-50 border border-red-200 flex items-center justify-center text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button onClick={() => setLines((cur) => [...cur, { item_id: "", quantity: 1 }])} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100" data-testid="lager-req-add-line"><Plus className="w-4 h-4" /> {t("cr.addline")}</button>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1.5">{t("note")}</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100">{t("cancel")}</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-sm font-black" style={{ color: "#fff" }} data-testid="lager-req-create-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("create")}</button>
        </div>
      </div>
    </div>
  );
}

function RequestsTab({ authHeader, isAdmin, items, refreshKey, onAddMore }) {
  const t = useLagerT();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [qrRequest, setQrRequest] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/doserne/lager/requests`, authHeader()); setRequests(data.requests || []); }
    catch { /* ignore */ }
    setLoading(false);
  }, [authHeader]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const toggleLine = async (r, idx, checked) => {
    try { await axios.post(`${API}/doserne/lager/requests/${r.id}/toggle-line`, { index: idx, checked }, authHeader()); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
  };

  const setStatus = async (r, status) => {
    try { await axios.post(`${API}/doserne/lager/requests/${r.id}/status`, { status }, authHeader()); toast.success(t("toast.updated")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
  };
  const del = async (r) => {
    if (!window.confirm(`${r.code}?`)) return;
    try { await axios.delete(`${API}/doserne/lager/requests/${r.id}`, authHeader()); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
  };
  const reopen = async (r) => {
    if (!window.confirm(t("corr.confirm", { code: r.code }))) return;
    try { await axios.post(`${API}/doserne/lager/requests/${r.id}/reopen`, {}, authHeader()); toast.success(t("corr.done")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
  };
  const setLineQty = async (r, idx, quantity) => {
    if (quantity < 1) return;
    try { await axios.patch(`${API}/doserne/lager/requests/${r.id}/line-qty`, { index: idx, quantity }, authHeader()); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
  };
  const setDelivery = async (r, dd) => {
    if (!dd) return;
    try { await axios.patch(`${API}/doserne/lager/requests/${r.id}/delivery-date`, { delivery_date: dd }, authHeader()); toast.success(t("toast.updated")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
  };

  return (
    <div data-testid="lager-requests-tab">
      {isAdmin && (
        <div className="mb-4">
          <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-sm font-black" style={{ color: "#fff" }} data-testid="lager-req-new"><Plus className="w-4 h-4" /> {t("req_new")}</button>
        </div>
      )}
      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : requests.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-500 italic" data-testid="lager-requests-empty">{t("req_empty")}</div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => {
            const tm = REQ_TYPE_META[r.type] || {}; const sm = REQ_STATUS_META[r.status] || {};
            return (
              <div key={r.id} className="bg-white border-2 border-slate-200 rounded-2xl p-4" data-testid={`lager-request-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-sm font-black text-slate-900 font-mono">{r.code}</span>
                  {r.list_name && <span className="text-sm font-black text-slate-800" data-testid={`lager-request-listname-${r.id}`}>· {r.list_name}</span>}
                  <span className={`text-[9px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 ${tm.cls}`}>{t(tm.key)}</span>
                  <span className={`text-[9px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 ${sm.cls}`} data-testid={`lager-request-status-${r.id}`}>{t(sm.key)}</span>
                  {r.project_name && <span className="text-xs text-cyan-700">📍 {r.project_name}</span>}
                  {isAdmin && r.type === "out" && !["done", "cancelled"].includes(r.status) ? (
                    <label className="inline-flex items-center gap-1 text-[11px] font-black text-teal-700 rounded-full bg-teal-50 border border-teal-200 px-2 py-0.5 cursor-pointer">
                      <CalendarDays className="w-3 h-3" />
                      <input type="date" value={r.delivery_date || ""} onChange={(e) => setDelivery(r, e.target.value)} className="bg-transparent text-teal-700 font-black focus:outline-none w-[112px]" data-testid={`lager-req-editdate-${r.id}`} />
                    </label>
                  ) : (r.delivery_date && <span className="inline-flex items-center gap-1 text-[11px] font-black text-teal-700 rounded-full bg-teal-50 border border-teal-200 px-2 py-0.5"><CalendarDays className="w-3 h-3" /> {t("delivery_on")}: {r.delivery_date}</span>)}
                  <span className="ml-auto text-[11px] text-slate-400">{r.created_by_name}</span>
                  <button onClick={() => setQrRequest(r)} className="w-8 h-8 rounded-lg border border-slate-300 hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid={`lager-req-qr-${r.id}`} title={t("qr.title")}><QrCode className="w-4 h-4" /></button>
                </div>
                {(() => {
                  const total = r.lines.length;
                  const done = r.lines.filter((l) => l.checked).length;
                  const kg = r.lines.reduce((s, l) => s + (Number(l.weight_kg) || 0) * (Number(l.quantity) || 0), 0);
                  return (
                    <div className="mb-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-black">{t("req_materials", { kg: kg.toFixed(1) })}</span>
                        <span className="text-[11px] font-bold text-slate-500" data-testid={`lager-req-progress-${r.id}`}>{t("req_progress", { d: done, t: total })}</span>
                      </div>
                      <div className="space-y-1">
                        {r.lines.map((l, idx) => {
                          const editable = isAdmin && !r.stock_applied && ["draft", "preparing", "ready"].includes(r.status);
                          return (
                            <div key={idx} className="w-full text-sm flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-50">
                              <button
                                onClick={() => toggleLine(r, idx, !l.checked)}
                                className="flex items-center gap-2 flex-1 min-w-0 text-left"
                                data-testid={`lager-req-line-${r.id}-${idx}`}
                              >
                                {l.checked
                                  ? <CheckSquare className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                  : <Square className="w-4 h-4 text-slate-300 flex-shrink-0" />}
                                <span className={`font-semibold ${l.checked ? "text-slate-400 line-through" : "text-slate-700"}`}>{l.item_name}</span>
                                {!editable && <span className="text-slate-500">· {l.quantity} {l.unit}</span>}
                              </button>
                              {editable && (
                                <span className="inline-flex items-center gap-1 flex-shrink-0">
                                  <button onClick={() => setLineQty(r, idx, (l.quantity || 1) - 1)} className="w-6 h-6 rounded border border-slate-200 text-slate-500 flex items-center justify-center hover:bg-slate-100" data-testid={`lager-req-lineqty-dec-${r.id}-${idx}`}><Minus className="w-3 h-3" /></button>
                                  <span className="w-8 text-center text-sm font-bold text-slate-800" data-testid={`lager-req-lineqty-${r.id}-${idx}`}>{l.quantity}</span>
                                  <button onClick={() => setLineQty(r, idx, (l.quantity || 1) + 1)} className="w-6 h-6 rounded border border-slate-200 text-slate-500 flex items-center justify-center hover:bg-slate-100" data-testid={`lager-req-lineqty-inc-${r.id}-${idx}`}><Plus className="w-3 h-3" /></button>
                                  <span className="text-[10px] text-slate-400 w-5">{l.unit}</span>
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                {r.note && <div className="text-xs text-slate-500 italic mb-2">„{r.note}"</div>}
                {r.type === "out" && r.status === "preparing" && !r.stock_applied && (
                  <button onClick={() => onAddMore(r.project_id, r.project_name)} className="mb-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-teal-300 bg-teal-50 hover:bg-teal-100 text-xs font-black text-teal-800" data-testid={`lager-req-addmore-${r.id}`}><Plus className="w-3.5 h-3.5" /> {t("add_more")}</button>
                )}
                {isAdmin && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {(NEXT_STATUS[r.status] || []).map(([st, lbl]) => (
                      <button key={st} onClick={() => setStatus(r, st)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-black ${st === "done" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : st === "cancelled" ? "border border-slate-300 text-slate-600 hover:bg-slate-100" : "bg-slate-900 hover:bg-slate-800 text-white"}`} style={st === "cancelled" ? {} : { color: "#fff" }} data-testid={`lager-req-to-${st}-${r.id}`}>
                        {st === "done" && <CheckCircle2 className="w-3.5 h-3.5" />} {t(lbl)}
                      </button>
                    ))}
                    {!r.stock_applied && (
                      <button onClick={() => del(r)} className="ml-auto w-8 h-8 rounded-lg hover:bg-red-50 border border-red-200 flex items-center justify-center text-red-500" data-testid={`lager-req-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                    {r.status === "done" && r.stock_applied && (
                      <button onClick={() => reopen(r)} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-black border border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`lager-req-correct-${r.id}`} title={t("corr.hint")}><RotateCcw className="w-3.5 h-3.5" /> {t("corr.btn")}</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {showCreate && <CreateRequestModal items={items} onClose={() => setShowCreate(false)} authHeader={authHeader} onCreated={load} />}
      {qrRequest && <QrModal request={qrRequest} onClose={() => setQrRequest(null)} />}
    </div>
  );
}

function QrModal({ request, onClose }) {
  const t = useLagerT();
  const url = `${window.location.origin}/lager/r/${request.code}`;
  const doPrint = () => {
    const canvas = document.querySelector("#lager-qr-canvas canvas");
    const img = canvas ? canvas.toDataURL("image/png") : "";
    const w = window.open("", "_blank", "width=420,height=620");
    if (!w) return;
    const rows = request.lines.map((l) => `<tr><td style="padding:4px 0;border-bottom:1px solid #eee">${l.item_name}</td><td style="text-align:right;color:#555">${l.quantity} ${l.unit}</td></tr>`).join("");
    w.document.write(`<html><head><title>${request.code}</title></head><body style="font-family:sans-serif;text-align:center;padding:24px">
      <h1 style="font-size:28px;margin:0;letter-spacing:1px">${request.code}</h1>
      ${request.project_name ? `<div style="color:#0891b2;font-weight:bold;margin:4px 0">📍 ${request.project_name}</div>` : ""}
      <img src="${img}" style="width:240px;height:240px;margin:16px auto" />
      <table style="width:100%;border-collapse:collapse;font-size:14px">${rows}</table>
      <p style="color:#888;font-size:11px;margin-top:16px">${t("qr.scanhint")}</p>
    </body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); }, 300);
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="lager-qr-modal">
      <div className="bg-white rounded-2xl w-full max-w-xs shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-black text-slate-900 font-mono">{request.code}</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5 flex flex-col items-center">
          <div id="lager-qr-canvas" className="p-3 bg-white border border-slate-200 rounded-xl">
            <QRCodeCanvas value={url} size={200} level="M" includeMargin={false} />
          </div>
          {request.project_name && <div className="mt-3 text-sm font-bold text-cyan-700">📍 {request.project_name}</div>}
          <div className="mt-1 text-xs text-slate-500 text-center">{t("qr.hint")}</div>
          <button onClick={doPrint} className="mt-4 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-sm font-black" style={{ color: "#fff" }} data-testid="lager-qr-print"><Printer className="w-4 h-4" /> {t("qr.print")}</button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// ACTIVE CARTS tab (admin/lager)
// -----------------------------------------------------------------
function ActiveCartsTab({ authHeader }) {
  const t = useLagerT();
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/doserne/lager/active-carts`, authHeader());
        setCarts(data.carts || []);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [authHeader]);

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (carts.length === 0) return <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-500 italic" data-testid="lager-active-empty">{t("active_empty")}</div>;

  return (
    <div className="space-y-3" data-testid="lager-active-tab">
      {carts.map((c) => (
        <div key={c.user_email} className="bg-white border-2 border-slate-200 rounded-2xl p-4" data-testid={`lager-active-cart-${c.user_email}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-black text-slate-900">{c.user_name}</span>
            {c.is_salg && <span className="text-[9px] uppercase tracking-widest font-black text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-1.5 py-0.5">SALG</span>}
            <span className="ml-auto text-xs text-slate-400">{t("active_total", { n: c.total })}</span>
          </div>
          {(() => {
            const groups = {};
            (c.lines || []).forEach((l) => {
              const key = l.project_name || "__none__";
              (groups[key] = groups[key] || []).push(l);
            });
            return (
              <div className="space-y-2.5">
                {Object.entries(groups).map(([pname, lines]) => (
                  <div key={pname} className="rounded-xl border border-slate-100 bg-slate-50/70 p-2.5" data-testid={`lager-active-list-${c.user_email}`}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <MapPin className="w-3.5 h-3.5 text-cyan-600 flex-shrink-0" />
                      <span className="text-xs font-black text-cyan-700">{pname === "__none__" ? t("no_project") : pname}</span>
                      <span className="ml-auto text-[10px] text-slate-400">{t("active_total", { n: lines.reduce((s, l) => s + (Number(l.quantity) || 0), 0) })}</span>
                    </div>
                    <div className="space-y-1">
                      {lines.map((l) => (
                        <div key={l.id} className="text-sm text-slate-700 flex items-center gap-2">
                          <Package className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                          <span className="font-semibold">{l.item_name}</span>
                          <span className="text-slate-500">· {l.quantity} {l.unit}</span>
                          {l.job_name && <span className="text-[11px] text-slate-400">· {l.job_name}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------
// HISTORY tab (admin/depot) — dispatched lists with a status filter.
// -----------------------------------------------------------------
function HistoryTab({ authHeader }) {
  const t = useLagerT();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("done");
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/doserne/lager/requests`, authHeader());
        setRequests((data.requests || []).filter((r) => r.type === "out"));
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [authHeader]);

  const FILTERS = [
    ["done", t("s.done")],
    ["all", t("history_all")],
    ["preparing", t("s.preparing")],
    ["ready", t("s.ready")],
    ["cancelled", t("s.cancelled")],
  ];
  const byStatus = filter === "all" ? requests : requests.filter((r) => r.status === filter);
  const needle = q.trim().toLowerCase();
  const shown = !needle ? byStatus : byStatus.filter((r) => {
    const hay = [r.code, r.list_name, r.project_name, r.created_by_name, r.delivery_date, ...(r.lines || []).map((l) => l.item_name)]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(needle);
  });

  return (
    <div data-testid="lager-history-tab">
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("history_search")}
          className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-slate-300 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-cyan-500"
          data-testid="lager-history-search"
        />
        {q && <button onClick={() => setQ("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400" data-testid="lager-history-search-clear"><X className="w-4 h-4" /></button>}
      </div>
      <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1">
        {FILTERS.map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-black border transition ${filter === key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
            data-testid={`lager-history-filter-${key}`}>{label}</button>
        ))}
      </div>
      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : shown.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-500 italic" data-testid="lager-history-empty">{t("history_empty")}</div>
      ) : (
        <div className="space-y-3">
          {shown.map((r) => {
            const sm = REQ_STATUS_META[r.status] || {};
            const kg = r.lines.reduce((s, l) => s + (Number(l.weight_kg) || 0) * (Number(l.quantity) || 0), 0);
            return (
              <div key={r.id} className="bg-white border-2 border-slate-200 rounded-2xl p-4" data-testid={`lager-history-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-sm font-black text-slate-900 font-mono">{r.code}</span>
                  {r.list_name && <span className="text-sm font-black text-slate-800">· {r.list_name}</span>}
                  <span className={`text-[9px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 ${sm.cls}`}>{t(sm.key)}</span>
                  {r.project_name && <span className="text-xs text-cyan-700">📍 {r.project_name}</span>}
                  {r.delivery_date && <span className="inline-flex items-center gap-1 text-[11px] font-black text-teal-700 rounded-full bg-teal-50 border border-teal-200 px-2 py-0.5"><CalendarDays className="w-3 h-3" /> {r.delivery_date}</span>}
                  <span className="ml-auto text-[11px] text-slate-400">{r.created_by_name}</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-1">{t("req_materials", { kg: kg.toFixed(1) })}</div>
                <div className="space-y-0.5">
                  {r.lines.map((l, idx) => (
                    <div key={idx} className="text-sm text-slate-700 flex items-center gap-2">
                      <Package className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                      <span className="font-semibold">{l.item_name}</span>
                      <span className="text-slate-500">· {l.quantity} {l.unit}</span>
                    </div>
                  ))}
                </div>
                {r.note && <div className="text-xs text-slate-500 italic mt-2">„{r.note}"</div>}
                {(r.photos || []).length > 0 && (
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {r.photos.map((p, i) => (
                      <a key={i} href={p} target="_blank" rel="noreferrer" className="block w-14 h-14 rounded-lg overflow-hidden border border-slate-200">
                        <img src={p} alt="" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// CATALOG ADMIN modal
// -----------------------------------------------------------------
function emptyItem() {
  return { id: null, name: "", name_no: "", name_en: "", sku: "", art_no: "", tripletex_code: "", category: "", system: "", material: "", color: "", size: "", weight_kg: 0, diameter_mm: 0, unit: "stk", quantity: 0, min_stock: 0, image_url: "", images: [], notes: "", sort_order: 100, active: true };
}

function TaxonomyModal({ authHeader, onClose, onChanged }) {
  const t = useLagerT();
  const [systems, setSystems] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/doserne/lager/taxonomy`, authHeader());
      setSystems(data.systems || []);
      setOrphans(data.orphan_categories || []);
    } catch { toast.error(t("toast.err")); }
    setLoading(false);
  }, [authHeader, t]);
  useEffect(() => { load(); }, [load]);

  const reorderSystems = async (order) => {
    setBusy(true);
    try { await axios.post(`${API}/doserne/lager/taxonomy/reorder`, { kind: "system", order }, authHeader()); await load(); onChanged?.(); }
    catch { toast.error(t("toast.err")); }
    setBusy(false);
  };
  const reorderCats = async (system, order) => {
    setBusy(true);
    try { await axios.post(`${API}/doserne/lager/taxonomy/reorder`, { kind: "category", system, order }, authHeader()); await load(); onChanged?.(); }
    catch { toast.error(t("toast.err")); }
    setBusy(false);
  };
  const rename = async (kind, system, old) => {
    const next = window.prompt(t("cat.rename_prompt"), old);
    if (!next || next.trim() === old) return;
    setBusy(true);
    try { await axios.post(`${API}/doserne/lager/taxonomy/rename`, { kind, system: system || "", old, new: next.trim() }, authHeader()); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
    setBusy(false);
  };
  const del = async (kind, system, name) => {
    if (!window.confirm(t("cat.delete_confirm", { name }))) return;
    setBusy(true);
    try { await axios.post(`${API}/doserne/lager/taxonomy/delete`, { kind, system: system || "", name }, authHeader()); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
    setBusy(false);
  };

  const [newSys, setNewSys] = useState("");
  const [newCat, setNewCat] = useState({});
  const dragRef = React.useRef(null);

  const add = async (kind, system, name) => {
    const nm = (name || "").trim();
    if (!nm) return;
    setBusy(true);
    try {
      await axios.post(`${API}/doserne/lager/taxonomy/add`, { kind, system: system || "", name: nm }, authHeader());
      if (kind === "system") setNewSys(""); else setNewCat((m) => ({ ...m, [system]: "" }));
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
    setBusy(false);
  };

  const onDrop = (kind, system, targetIdx) => {
    const d = dragRef.current; dragRef.current = null;
    if (!d || d.kind !== kind || (kind === "category" && d.system !== system)) return;
    const arr = kind === "system"
      ? systems.map((x) => x.name)
      : (systems.find((s) => s.name === system)?.categories || []).map((x) => x.name);
    if (d.index === targetIdx || d.index < 0 || d.index >= arr.length) return;
    const copy = [...arr];
    const [moved] = copy.splice(d.index, 1);
    copy.splice(targetIdx, 0, moved);
    if (kind === "system") reorderSystems(copy); else reorderCats(system, copy);
  };

  const moveInArr = (arr, i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return null;
    const copy = [...arr];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  };

  const RowActions = ({ onUp, onDown, onRename, onDelete, canUp, canDown, count }) => (
    <div className="flex items-center gap-1">
      <button onClick={onUp} disabled={!canUp || busy} className="w-7 h-7 rounded-lg hover:bg-slate-100 disabled:opacity-30 flex items-center justify-center"><ChevronRight className="w-4 h-4 -rotate-90 text-slate-600" /></button>
      <button onClick={onDown} disabled={!canDown || busy} className="w-7 h-7 rounded-lg hover:bg-slate-100 disabled:opacity-30 flex items-center justify-center"><ChevronRight className="w-4 h-4 rotate-90 text-slate-600" /></button>
      <button onClick={onRename} disabled={busy} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center" title={t("cat.rename")}><Pencil className="w-3.5 h-3.5 text-slate-600" /></button>
      <button onClick={onDelete} disabled={busy} className={`w-7 h-7 rounded-lg flex items-center justify-center ${count > 0 ? "opacity-40" : "hover:bg-red-50"}`} title={count > 0 ? t("cat.has_items", { n: count }) : t("cat.delete")}><Trash2 className={`w-3.5 h-3.5 ${count > 0 ? "text-slate-400" : "text-red-500"}`} /></button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="lager-taxonomy-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg h-[90vh] sm:h-[80vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-black">Admin</div>
            <h2 className="text-lg font-black text-slate-900">{t("cat.organize")}</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <>
              <p className="text-[11px] text-slate-500">{t("cat.help")}</p>
              {/* Add a new top-level system */}
              <div className="flex items-center gap-2">
                <input value={newSys} onChange={(e) => setNewSys(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add("system", "", newSys)}
                  placeholder={t("cat.new_system")} className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg" data-testid="tax-new-system-input" />
                <button onClick={() => add("system", "", newSys)} disabled={busy || !newSys.trim()} className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-black disabled:opacity-40" style={{ color: "#fff" }} data-testid="tax-add-system"><Plus className="w-4 h-4" /></button>
              </div>
              {systems.map((s, si) => (
                <div key={s.name} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`tax-system-${s.name}`}>
                  <div draggable onDragStart={() => { dragRef.current = { kind: "system", index: si }; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop("system", "", si)}
                    className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100 cursor-grab active:cursor-grabbing">
                    <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0" />
                    <span className="flex-1 font-black text-slate-900 text-sm truncate">{s.name} <span className="text-[10px] font-bold text-slate-400">({s.count})</span></span>
                    <RowActions
                      canUp={si > 0} canDown={si < systems.length - 1} count={s.count}
                      onUp={() => { const o = moveInArr(systems.map((x) => x.name), si, -1); if (o) reorderSystems(o); }}
                      onDown={() => { const o = moveInArr(systems.map((x) => x.name), si, 1); if (o) reorderSystems(o); }}
                      onRename={() => rename("system", "", s.name)}
                      onDelete={() => del("system", "", s.name)}
                    />
                  </div>
                  <div className="divide-y divide-slate-50">
                    {s.categories.map((c, ci) => (
                      <div key={c.name} draggable onDragStart={() => { dragRef.current = { kind: "category", system: s.name, index: ci }; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop("category", s.name, ci)}
                        className="flex items-center gap-2 px-3 py-1.5 pl-5 cursor-grab active:cursor-grabbing" data-testid={`tax-cat-${s.name}-${c.name}`}>
                        <GripVertical className="w-3.5 h-3.5 text-slate-200 flex-shrink-0" />
                        <span className="flex-1 text-sm text-slate-700 truncate">{c.name} <span className="text-[10px] font-bold text-slate-400">({c.count})</span></span>
                        <RowActions
                          canUp={ci > 0} canDown={ci < s.categories.length - 1} count={c.count}
                          onUp={() => { const o = moveInArr(s.categories.map((x) => x.name), ci, -1); if (o) reorderCats(s.name, o); }}
                          onDown={() => { const o = moveInArr(s.categories.map((x) => x.name), ci, 1); if (o) reorderCats(s.name, o); }}
                          onRename={() => rename("category", s.name, c.name)}
                          onDelete={() => del("category", s.name, c.name)}
                        />
                      </div>
                    ))}
                    {s.categories.length === 0 && <div className="px-5 py-1.5 text-[11px] text-slate-400 italic">{t("cat.no_subs")}</div>}
                    {/* Add a sub-category to this system */}
                    <div className="flex items-center gap-2 px-3 py-2 pl-5 bg-slate-50/40">
                      <input value={newCat[s.name] || ""} onChange={(e) => setNewCat((m) => ({ ...m, [s.name]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && add("category", s.name, newCat[s.name])}
                        placeholder={t("cat.new_sub")} className="flex-1 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg" data-testid={`tax-new-cat-input-${s.name}`} />
                      <button onClick={() => add("category", s.name, newCat[s.name])} disabled={busy || !(newCat[s.name] || "").trim()} className="px-2.5 py-1.5 rounded-lg bg-cyan-600 text-white text-xs font-black disabled:opacity-40" style={{ color: "#fff" }} data-testid={`tax-add-cat-${s.name}`}><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
              {orphans.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/50 overflow-hidden">
                  <div className="px-3 py-2 text-xs font-black text-amber-800 border-b border-amber-100">{t("cat.no_system")}</div>
                  {orphans.map((c) => (
                    <div key={c.name} className="flex items-center gap-2 px-3 py-1.5">
                      <span className="flex-1 text-sm text-slate-700 truncate">{c.name} <span className="text-[10px] font-bold text-slate-400">({c.count})</span></span>
                      <button onClick={() => rename("category", "", c.name)} disabled={busy} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center"><Pencil className="w-3.5 h-3.5 text-slate-600" /></button>
                      <button onClick={() => del("category", "", c.name)} disabled={busy} className={`w-7 h-7 rounded-lg flex items-center justify-center ${c.count > 0 ? "opacity-40" : "hover:bg-red-50"}`}><Trash2 className={`w-3.5 h-3.5 ${c.count > 0 ? "text-slate-400" : "text-red-500"}`} /></button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function CatalogAdmin({ onClose, authHeader, onChanged }) {
  const t = useLagerT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [salgInput, setSalgInput] = useState("");
  const [salgEmails, setSalgEmails] = useState([]);
  const [depotEmails, setDepotEmails] = useState([]);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [filterSystem, setFilterSystem] = useState("__all__");
  const [bulkSystem, setBulkSystem] = useState("");
  const [bulkCat, setBulkCat] = useState("");
  const [showTax, setShowTax] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, salg, depot, mem] = await Promise.all([
        axios.get(`${API}/doserne/lager/catalog?include_inactive=true`, authHeader()),
        axios.get(`${API}/doserne/lager/admin/salg`, authHeader()),
        axios.get(`${API}/doserne/lager/admin/depot`, authHeader()),
        axios.get(`${API}/doserne/lager/admin/members`, authHeader()),
      ]);
      setItems(cat.data.items || []);
      setSalgEmails(salg.data.emails || []);
      setDepotEmails(depot.data.emails || []);
      setMembers(mem.data.members || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [authHeader]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      if (editing.id) await axios.put(`${API}/doserne/lager/catalog/${editing.id}`, editing, authHeader());
      else await axios.post(`${API}/doserne/lager/catalog`, editing, authHeader());
      toast.success(t("toast.saved"));
      setEditing(null); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
    setBusy(false);
  };
  const remove = async (it) => {
    if (!window.confirm(`${it.name}?`)) return;
    try { await axios.delete(`${API}/doserne/lager/catalog/${it.id}?permanent=true`, authHeader()); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
  };
  const saveSalg = async (emails) => {
    try { await axios.put(`${API}/doserne/lager/admin/salg`, { emails }, authHeader()); setSalgEmails(emails); }
    catch { toast.error(t("toast.err")); }
  };
  const toggleDepot = async (email) => {
    const next = depotEmails.includes(email) ? depotEmails.filter((x) => x !== email) : [...depotEmails, email];
    setDepotEmails(next);
    try { await axios.put(`${API}/doserne/lager/admin/depot`, { emails: next }, authHeader()); }
    catch { toast.error(t("toast.err")); setDepotEmails(depotEmails); }
  };
  const catSystems = Array.from(new Set(items.map((i) => (i.system || "").trim()).filter(Boolean))).sort();
  const shown = filterSystem === "__all__" ? items : items.filter((i) => (i.system || "") === filterSystem);
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allShownSelected = shown.length > 0 && shown.every((i) => selected.has(i.id));
  const toggleAllShown = () => setSelected((s) => {
    const n = new Set(s);
    if (allShownSelected) shown.forEach((i) => n.delete(i.id));
    else shown.forEach((i) => n.add(i.id));
    return n;
  });
  const applyBulk = async () => {
    const ids = Array.from(selected);
    if (!ids.length || (!bulkSystem.trim() && !bulkCat.trim())) { toast.error(t("bulk.pick")); return; }
    setBusy(true);
    try {
      const body = { ids };
      if (bulkSystem.trim()) body.system = bulkSystem.trim();
      if (bulkCat.trim()) body.category = bulkCat.trim();
      const { data } = await axios.post(`${API}/doserne/lager/catalog/bulk-categorize`, body, authHeader());
      toast.success(t("bulk.done", { n: data.updated }));
      setSelected(new Set()); setBulkSystem(""); setBulkCat("");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="lager-catalog-admin">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-3xl h-[90vh] sm:h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-black">Admin</div>
            <h2 className="text-xl font-black text-slate-900">{t("cat.title")}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowTax(true)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-black" data-testid="lager-organize-cats"><Settings2 className="w-4 h-4" /> {t("cat.organize")}</button>
            <button onClick={() => setEditing(emptyItem())} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-sm font-black" style={{ color: "#fff" }} data-testid="lager-catalog-add"><Plus className="w-4 h-4" /> {t("cat.new")}</button>
            <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5 text-slate-500" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <>
              {/* Reorganize into systems / sub-categories in bulk */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-black">{t("h.system")}:</span>
                <button onClick={() => setFilterSystem("__all__")} className={`text-xs font-bold px-3 py-1 rounded-full border ${filterSystem === "__all__" ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-slate-600 border-slate-200"}`}>{t("all_systems")}</button>
                {catSystems.map((s) => (
                  <button key={s} onClick={() => setFilterSystem(s)} className={`text-xs font-bold px-3 py-1 rounded-full border ${filterSystem === s ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-slate-600 border-slate-200"}`}>{s}</button>
                ))}
              </div>
              {selected.size > 0 && (
                <div className="sticky top-0 z-10 flex items-center gap-2 flex-wrap mb-3 rounded-xl border-2 border-cyan-300 bg-cyan-50 px-3 py-2" data-testid="lager-bulk-bar">
                  <span className="text-xs font-black text-cyan-800">{t("bulk.selected", { n: selected.size })}</span>
                  <input value={bulkSystem} onChange={(e) => setBulkSystem(e.target.value)} list="lager-bulk-systems" placeholder={`${t("h.system")} (Haki…)`} className="px-2 py-1 border border-slate-300 rounded-lg text-sm text-slate-900 w-28" data-testid="lager-bulk-system" />
                  <datalist id="lager-bulk-systems">{[...SYSTEMS, ...catSystems].map((s) => <option key={s} value={s} />)}</datalist>
                  <input value={bulkCat} onChange={(e) => setBulkCat(e.target.value)} placeholder={`${t("subcategory")} (Frames…)`} className="px-2 py-1 border border-slate-300 rounded-lg text-sm text-slate-900 w-32" data-testid="lager-bulk-cat" />
                  <button onClick={applyBulk} disabled={busy} className="px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-sm font-black" style={{ color: "#fff" }} data-testid="lager-bulk-apply">{t("bulk.apply")}</button>
                  <button onClick={() => setSelected(new Set())} className="text-xs text-slate-500 hover:text-slate-800 underline">{t("bulk.clear")}</button>
                </div>
              )}
              <table className="w-full text-sm mb-6">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  <tr>
                    <th className="px-2 py-2 w-8"><button onClick={toggleAllShown} data-testid="lager-bulk-selectall">{allShownSelected ? <CheckSquare className="w-4 h-4 text-cyan-600" /> : <Square className="w-4 h-4 text-slate-300" />}</button></th>
                    <th className="text-left px-3 py-2">{t("h.name")}</th>
                    <th className="px-3 py-2 w-20">{t("h.stock")}</th>
                    <th className="px-3 py-2 w-20">Min</th>
                    <th className="px-3 py-2 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((it) => (
                    <tr key={it.id} className={`border-b border-slate-100 ${selected.has(it.id) ? "bg-cyan-50/50" : ""}`} data-testid={`lager-catalog-row-${it.id}`}>
                      <td className="px-2 py-2"><button onClick={() => toggleSel(it.id)} data-testid={`lager-bulk-check-${it.id}`}>{selected.has(it.id) ? <CheckSquare className="w-4 h-4 text-cyan-600" /> : <Square className="w-4 h-4 text-slate-300" />}</button></td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-900">{it.name} {!it.active && <span className="text-[9px] text-slate-400">{t("cat.hidden")}</span>}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{it.sku || "—"} · <span className="text-cyan-700">{it.system || "—"}</span> › {it.category || "—"}</div>
                      </td>
                      <td className="px-3 py-2 text-center font-black text-slate-800">{it.quantity} {it.unit}</td>
                      <td className="px-3 py-2 text-center text-slate-500">{it.min_stock}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setEditing({ ...it, images: it.images || [] })} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid={`lager-catalog-edit-${it.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => remove(it)} className="w-8 h-8 rounded-lg hover:bg-red-50 border border-red-200 flex items-center justify-center text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="lager-salg-section">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {t("salg.title")}</div>
                <p className="text-xs text-slate-500 mb-3">{t("salg.desc")}</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {salgEmails.map((e) => (
                    <span key={e} className="inline-flex items-center gap-1 text-xs font-bold text-cyan-800 bg-white border border-cyan-200 rounded-full px-2 py-0.5">
                      {e}
                      <button onClick={() => saveSalg(salgEmails.filter((x) => x !== e))} className="text-cyan-400 hover:text-red-500" data-testid={`lager-salg-remove-${e}`}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {salgEmails.length === 0 && <span className="text-xs text-slate-400 italic">{t("salg.empty")}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <input value={salgInput} onChange={(e) => setSalgInput(e.target.value)} placeholder="email@doserne.com" className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-salg-input" />
                  <button
                    onClick={() => { const e = salgInput.trim().toLowerCase(); if (e && !salgEmails.includes(e)) { saveSalg([...salgEmails, e]); setSalgInput(""); } }}
                    className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-sm font-black" style={{ color: "#fff" }} data-testid="lager-salg-add"
                  >{t("salg.add")}</button>
                </div>
              </div>

              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mt-4" data-testid="lager-depot-section">
                <div className="text-[10px] uppercase tracking-widest text-amber-700 font-black mb-1 flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" /> {t("depot.title")}</div>
                <p className="text-xs text-amber-700/80 mb-3">{t("depot.desc")}</p>
                <div className="space-y-1">
                  {members.map((m) => {
                    const on = depotEmails.includes(m.email);
                    return (
                      <button key={m.email} onClick={() => toggleDepot(m.email)} className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left border ${on ? "border-amber-400 bg-white" : "border-transparent hover:bg-white/60"}`} data-testid={`lager-depot-toggle-${m.email}`}>
                        {on ? <CheckSquare className="w-4 h-4 text-amber-600 flex-shrink-0" /> : <Square className="w-4 h-4 text-slate-300 flex-shrink-0" />}
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-bold text-slate-800 truncate">{m.name}</span>
                          <span className="block text-[11px] text-slate-400 font-mono truncate">{m.email}</span>
                        </span>
                        {on && <span className="text-[9px] uppercase tracking-widest font-black text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-1.5 py-0.5">{t("depot.badge")}</span>}
                      </button>
                    );
                  })}
                  {members.length === 0 && <span className="text-xs text-slate-400 italic">{t("depot.empty")}</span>}
                </div>
              </div>
            </>
          )}
        </div>

        {editing && (
          <CatalogEditor draft={editing} setDraft={setEditing} authHeader={authHeader} onClose={() => setEditing(null)} onSave={save} busy={busy} allItems={items} />
        )}
        {showTax && <TaxonomyModal authHeader={authHeader} onClose={() => setShowTax(false)} onChanged={() => { load(); onChanged?.(); }} />}
      </div>
    </div>
  );
}

function CatalogEditor({ draft, setDraft, authHeader, onClose, onSave, busy, allItems = [] }) {
  const t = useLagerT();
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setDraft((c) => ({ ...c, [k]: v }));
  const images = draft.images && draft.images.length ? draft.images : (draft.image_url ? [draft.image_url] : []);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/doserne/lager/admin/upload-image`, form, {
        ...authHeader(), headers: { ...(authHeader().headers || {}), "Content-Type": "multipart/form-data" },
      });
      if (data.url) setDraft((c) => ({ ...c, images: [data.url], image_url: data.url }));
    } catch (err) { toast.error(err?.response?.data?.detail || t("toast.err")); }
    setUploading(false);
  };

  const systemSuggestions = Array.from(new Set([...SYSTEMS, ...allItems.map((i) => (i.system || "").trim()).filter(Boolean)]));
  const catSuggestions = Array.from(new Set(
    allItems.filter((i) => !draft.system || (i.system || "") === draft.system).map((i) => (i.category || "").trim()).filter(Boolean),
  )).sort();

  return (
    <div className="absolute inset-0 bg-white flex flex-col rounded-t-2xl sm:rounded-2xl" data-testid="lager-catalog-editor">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-lg font-black text-slate-900">{draft.id ? t("e.edit") : t("cat.new")}</h3>
        <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5 text-slate-500" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("e.name")}</label>
          <input value={draft.name} onChange={(e) => set("name", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-editor-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">Nume NO 🇳🇴</label>
            <input value={draft.name_no || ""} onChange={(e) => set("name_no", e.target.value)} placeholder="Norsk navn" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-editor-name-no" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">Nume EN 🇬🇧</label>
            <input value={draft.name_en || ""} onChange={(e) => set("name_en", e.target.value)} placeholder="English name" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-editor-name-en" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">SKU</label>
            <input value={draft.sku} onChange={(e) => set("sku", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono text-slate-900" data-testid="lager-editor-sku" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("e.unit")}</label>
            <input value={draft.unit} onChange={(e) => set("unit", e.target.value)} placeholder="stk" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-editor-unit" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("l.category")} ({t("subcategory")})</label>
            <input value={draft.category} onChange={(e) => set("category", e.target.value)} list="lager-subcat-suggestions" placeholder="Universal, Frames…" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-editor-category" />
            <datalist id="lager-subcat-suggestions">{catSuggestions.map((c) => <option key={c} value={c} />)}</datalist>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("h.system")} ({t("main_category")})</label>
            <input value={draft.system || ""} onChange={(e) => set("system", e.target.value)} list="lager-system-suggestions" placeholder="Haki, Ubix, Layher…" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-editor-system" />
            <datalist id="lager-system-suggestions">{systemSuggestions.map((s) => <option key={s} value={s} />)}</datalist>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("h.material")}</label>
            <input value={draft.material || ""} onChange={(e) => set("material", e.target.value)} placeholder="Oțel galvanizat" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-editor-material" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("h.color")}</label>
            <select value={draft.color || ""} onChange={(e) => set("color", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900" data-testid="lager-editor-color">
              <option value="">{t("e.none")}</option>
              {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("e.artno")}</label>
            <input value={draft.art_no || ""} onChange={(e) => set("art_no", e.target.value)} placeholder="S 1000" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono text-slate-900" data-testid="lager-editor-artno" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">Cod Tripletex</label>
            <input value={draft.tripletex_code || ""} onChange={(e) => set("tripletex_code", e.target.value)} placeholder="ex. 12345" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono text-slate-900" data-testid="lager-editor-tripletex" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("l.size")}</label>
            <input value={draft.size || ""} onChange={(e) => set("size", e.target.value)} placeholder="1.0 m" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-editor-size" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("l.weight")} (kg)</label>
            <input type="number" min={0} step="0.1" value={draft.weight_kg || 0} onChange={(e) => set("weight_kg", Number(e.target.value) || 0)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-editor-weight" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("l.diameter")} (mm)</label>
            <input type="number" min={0} value={draft.diameter_mm || 0} onChange={(e) => set("diameter_mm", Number(e.target.value) || 0)} placeholder="48" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-editor-diameter" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("e.stock")}</label>
            <input type="number" min={0} value={draft.quantity} onChange={(e) => set("quantity", Number(e.target.value) || 0)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-editor-quantity" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("e.minstock")}</label>
            <input type="number" min={0} value={draft.min_stock} onChange={(e) => set("min_stock", Number(e.target.value) || 0)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="lager-editor-minstock" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{t("e.image")}</label>
          <div className="flex items-center gap-2">
            {images[0] && <div className="w-20 h-20 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden"><img src={images[0]} alt="" className="w-full h-full object-cover" /></div>}
            <label className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-cyan-500 flex flex-col items-center justify-center gap-0.5 cursor-pointer text-slate-500" data-testid="lager-editor-upload">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span className="text-[9px] font-bold">{uploading ? "…" : t("e.upload")}</span>
              <input type="file" accept="image/*" onChange={uploadImage} className="hidden" disabled={uploading} />
            </label>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
          <input type="checkbox" checked={draft.active} onChange={(e) => set("active", e.target.checked)} data-testid="lager-editor-active" /> {t("e.visible")}
        </label>
      </div>
      <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100">{t("cancel")}</button>
        <button onClick={onSave} disabled={busy || !draft.name.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-sm font-black" style={{ color: "#fff" }} data-testid="lager-editor-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("e.save")}
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// PAGE
// -----------------------------------------------------------------
// -----------------------------------------------------------------
// Control overview (KPI strip) + Low-stock tab
// -----------------------------------------------------------------
const OV_TONE = {
  slate: { text: "text-slate-700", ring: "hover:border-slate-400", icon: "text-slate-400", bg: "bg-slate-50" },
  emerald: { text: "text-emerald-600", ring: "hover:border-emerald-400", icon: "text-emerald-500", bg: "bg-emerald-50" },
  amber: { text: "text-amber-600", ring: "hover:border-amber-400", icon: "text-amber-500", bg: "bg-amber-50" },
  red: { text: "text-red-600", ring: "hover:border-red-400", icon: "text-red-500", bg: "bg-red-50" },
  cyan: { text: "text-teal-700", ring: "hover:border-teal-400", icon: "text-teal-600", bg: "bg-teal-50" },
  violet: { text: "text-violet-600", ring: "hover:border-violet-400", icon: "text-violet-500", bg: "bg-violet-50" },
};

function OverviewBar({ items, reqOpen, activeCarts, isAdmin, onGo }) {
  const t = useLagerT();
  const total = items.length;
  const soldOut = items.filter((i) => i.available < 1).length;
  const low = items.filter((i) => i.low_stock).length;
  const inStock = items.filter((i) => i.available >= 1).length;
  const cards = [
    { go: "shop", label: t("ov.total"), value: total, Icon: Boxes, tone: "slate" },
    { go: "shop", label: t("ov.instock"), value: inStock, Icon: Package, tone: "emerald" },
    { go: "lowstock", label: t("ov.low"), value: low, Icon: AlertTriangle, tone: "amber" },
    { go: "lowstock", label: t("ov.out"), value: soldOut, Icon: X, tone: "red" },
    { go: "requests", label: t("ov.reqs"), value: reqOpen, Icon: ClipboardList, tone: "cyan" },
    ...(isAdmin ? [{ go: "active", label: t("ov.carts"), value: activeCarts, Icon: Users, tone: "violet" }] : []),
  ];
  return (
    <div className="mb-5" data-testid="lager-overview">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-2">{t("ov.title")}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
        {cards.map((c, idx) => {
          const tone = OV_TONE[c.tone];
          return (
            <button
              key={idx}
              onClick={() => onGo(c.go)}
              className={`text-left rounded-2xl border border-slate-200 bg-white p-3 transition ${tone.ring} hover:shadow-sm`}
              data-testid={`lager-ov-${c.tone}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className={`w-7 h-7 rounded-lg ${tone.bg} flex items-center justify-center`}>
                  <c.Icon className={`w-4 h-4 ${tone.icon}`} />
                </span>
                <span className={`text-2xl font-black tabular-nums ${tone.text}`}>{c.value}</span>
              </div>
              <div className="text-[11px] font-black text-slate-500 leading-tight">{c.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LowStockTab({ items }) {
  const t = useLagerT();
  const low = items.filter((i) => i.low_stock);
  if (low.length === 0) {
    return <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-500 italic" data-testid="lager-lowstock-empty">{t("lowstock_empty")}</div>;
  }
  return (
    <div data-testid="lager-lowstock-tab">
      <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <span className="flex items-center gap-1.5 text-amber-800 font-black"><AlertTriangle className="w-4 h-4" /> {t("low_stock", { n: low.length })}</span>
        <span className="text-slate-400">· {t("lowstock_desc")}</span>
      </div>
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2.5">{t("h.name")}</th>
                <th className="text-left px-3 py-2.5">{t("h.system")}</th>
                <th className="text-center px-3 py-2.5">{t("h.stock")}</th>
                <th className="text-center px-3 py-2.5">{t("h.avail")}</th>
              </tr>
            </thead>
            <tbody>
              {low.map((i) => {
                const soldOut = i.available < 1;
                return (
                  <tr key={i.id} className="border-b border-slate-100 hover:bg-amber-50/40" data-testid={`lager-lowstock-row-${i.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-black text-slate-900">{i.name}</div>
                      {(i.art_no || i.sku) && <div className="text-[10px] text-slate-400 font-mono">{i.art_no || i.sku}</div>}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{i.system || "—"}</td>
                    <td className="px-3 py-2 text-center font-bold text-slate-800">{i.quantity} {i.unit}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-xs font-black px-2 py-0.5 rounded ${soldOut ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{i.available} {i.unit}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RequestPhotosWeb({ req, authHeader, onReload }) {
  const t = useLagerT();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  const photos = req.photos || [];
  const upload = async (file) => {
    if (!file || !file.type?.startsWith("image/")) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/doserne/lager/requests/${req.id}/photos`, form, {
        ...authHeader(), headers: { ...(authHeader().headers || {}), "Content-Type": "multipart/form-data" },
      });
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
    setBusy(false);
  };
  const del = async (url) => {
    try { await axios.delete(`${API}/doserne/lager/requests/${req.id}/photos`, { ...authHeader(), data: { url } }); onReload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-black">{t("photos.title")}</span>
        <button onClick={() => fileRef.current?.click()} disabled={busy} className="inline-flex items-center gap-1 text-xs font-black text-white bg-slate-900 hover:bg-slate-800 disabled:opacity-50 rounded-lg px-2.5 py-1" data-testid={`lager-photo-add-${req.id}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />} {t("photos.add")}
        </button>
      </div>
      {photos.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {photos.map((p) => (
            <div key={p} className="relative group">
              <img src={p} alt="" onClick={() => setZoom(p)} className="w-14 h-14 rounded-lg object-cover border border-slate-200 cursor-pointer" data-testid={`lager-photo-thumb-${req.id}`} />
              <button onClick={() => del(p)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100" data-testid={`lager-photo-del-${req.id}`}><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setZoom(null)}>
          <img src={zoom} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
}

function DepotTab({ authHeader, refreshKey }) {
  const t = useLagerT();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/doserne/lager/requests`, authHeader());
      const open = (data.requests || []).filter((r) => r.type === "out" && (r.status === "preparing" || r.status === "ready"));
      setRequests(open);
    } catch { /* ignore */ }
    setLoading(false);
  }, [authHeader]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const toggleLine = async (r, idx, checked) => {
    try { await axios.post(`${API}/doserne/lager/requests/${r.id}/toggle-line`, { index: idx, checked }, authHeader()); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
  };
  const setStatus = async (r, status) => { try { await axios.post(`${API}/doserne/lager/requests/${r.id}/status`, { status }, authHeader()); toast.success(t("toast.updated")); load(); } catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); } };
  const setDelivery = async (r, dd) => { if (!dd) return; try { await axios.patch(`${API}/doserne/lager/requests/${r.id}/delivery-date`, { delivery_date: dd }, authHeader()); load(); } catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); } };

  const dateLabel = (iso) => {
    if (!iso) return t("depot.no_date");
    const today = new Date().toISOString().slice(0, 10);
    const tmr = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    if (iso === today) return t("depot.today");
    if (iso === tmr) return t("depot.tomorrow");
    return iso;
  };
  // Group the packing queue by delivery date (already sorted soonest-first).
  const groups = [];
  const seen = {};
  for (const r of requests) {
    const k = r.delivery_date || "";
    if (!(k in seen)) { seen[k] = groups.length; groups.push({ date: k, rows: [] }); }
    groups[seen[k]].rows.push(r);
  }

  return (
    <div data-testid="lager-depot-tab">
      <div className="mb-4 flex items-center gap-2">
        <PackageCheck className="w-5 h-5 text-amber-600" />
        <div>
          <div className="text-sm font-black text-slate-900">{t("depot.heading")}</div>
          <div className="text-xs text-slate-500">{t("depot.subheading")}</div>
        </div>
      </div>
      {(() => {
        const urgent = requests.filter((r) => r.is_urgent).length;
        if (!urgent) return null;
        return (
          <div className="mb-4 flex items-center gap-2 rounded-xl border-2 border-red-200 bg-red-50 px-4 py-3" data-testid="lager-depot-alert">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-sm font-black text-red-700">{t("depot.alert", { n: urgent })}</span>
          </div>
        );
      })()}
      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : requests.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-500 italic" data-testid="lager-depot-empty">{t("depot.empty_queue")}</div>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => {
            const urgentGroup = g.rows.some((r) => r.is_urgent);
            return (
            <div key={g.date || "none"}>
              <div className="flex items-center gap-2 mb-2">
                <CalendarDays className={`w-4 h-4 ${urgentGroup ? "text-red-500" : "text-amber-600"}`} />
                <span className={`text-sm font-black ${urgentGroup ? "text-red-600" : "text-slate-900"}`}>{dateLabel(g.date)}</span>
                <span className="text-xs text-slate-400">· {g.rows.length}</span>
              </div>
              <div className="space-y-3">
                {g.rows.map((r) => {
                  const total = r.lines.length;
                  const done = r.lines.filter((l) => l.checked).length;
                  const allDone = total > 0 && done === total;
                  return (
                    <div key={r.id} className="bg-white border-2 border-slate-200 rounded-2xl p-4" data-testid={`lager-depot-request-${r.id}`}>
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="text-sm font-black text-slate-900 font-mono">{r.code}</span>
                        <span className={`text-[9px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 ${(REQ_STATUS_META[r.status] || {}).cls}`}>{t((REQ_STATUS_META[r.status] || {}).key)}</span>
                        {r.project_name && <span className="text-xs text-cyan-700">📍 {r.project_name}</span>}
                        <label className="inline-flex items-center gap-1 text-[11px] font-black text-teal-700 rounded-full bg-teal-50 border border-teal-200 px-2 py-0.5 cursor-pointer">
                          <CalendarDays className="w-3 h-3" />
                          <input type="date" value={r.delivery_date || ""} onChange={(e) => setDelivery(r, e.target.value)} className="bg-transparent text-teal-700 font-black focus:outline-none w-[112px]" data-testid={`lager-depot-editdate-${r.id}`} />
                        </label>
                        <span className="ml-auto text-[11px] font-bold text-slate-500">{t("req_progress", { d: done, t: total })}</span>
                      </div>
                      <div className="space-y-1 mb-3">
                        {r.lines.map((l, idx) => (
                          <button key={idx} onClick={() => toggleLine(r, idx, !l.checked)} className="w-full text-sm flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-50 text-left" data-testid={`lager-depot-line-${r.id}-${idx}`}>
                            {l.checked ? <CheckSquare className="w-4 h-4 text-emerald-600 flex-shrink-0" /> : <Square className="w-4 h-4 text-slate-300 flex-shrink-0" />}
                            <span className={`font-semibold ${l.checked ? "text-slate-400 line-through" : "text-slate-700"}`}>{l.item_name}</span>
                            <span className="text-slate-500">· {l.quantity} {l.unit}</span>
                            {l.job_name && <span className="text-[10px] text-slate-400">· {l.job_name}</span>}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {r.status === "preparing" && (
                          <button onClick={() => setStatus(r, "ready")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-black bg-slate-900 hover:bg-slate-800" style={{ color: "#fff" }} data-testid={`lager-depot-ready-${r.id}`}><PackageCheck className="w-3.5 h-3.5" /> {t("depot.mark_ready")}</button>
                        )}
                        {r.status === "ready" && (
                          <button onClick={() => setStatus(r, "done")} disabled={!allDone} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-black bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40" style={{ color: "#fff" }} data-testid={`lager-depot-done-${r.id}`} title={allDone ? "" : t("depot.check_all_first")}><CheckCircle2 className="w-3.5 h-3.5" /> {t("depot.mark_delivered")}</button>
                        )}
                      </div>
                      <RequestPhotosWeb req={r} authHeader={authHeader} onReload={load} />
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const MONTHS_RO = ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// SALES tab (admin) — monthly/yearly sold quantities with a bar chart + top list.
function SalesTab({ authHeader }) {
  const t = useLagerT();
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [years, setYears] = useState([String(now.getFullYear())]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  // Discover which years have sales (once).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/lager/sales/report?period=all`, authHeader());
        const ys = Array.from(new Set((data.by_month || []).map((m) => (m.month || "").slice(0, 4)).filter(Boolean)));
        if (!ys.includes(String(now.getFullYear()))) ys.push(String(now.getFullYear()));
        ys.sort((a, b) => Number(b) - Number(a));
        setYears(ys);
        setYear(ys[0]);
      } catch { /* ignore */ }
    })();
  }, [authHeader]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/doserne/lager/sales/report?period=year&year=${year}`, authHeader());
      setReport(data);
    } catch { setReport(null); }
    setLoading(false);
  }, [authHeader, year]);
  useEffect(() => { load(); }, [load]);

  const monthMap = {};
  (report?.by_month || []).forEach((m) => { monthMap[m.month] = m.sold; });
  const chartData = MONTHS_RO.map((label, i) => ({
    label, sold: monthMap[`${year}-${String(i + 1).padStart(2, "0")}`] || 0,
  }));
  const total = report?.total_sold || 0;
  const topItems = report?.by_item || [];

  return (
    <div className="space-y-5" data-testid="lager-sales-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-cyan-700" />
          <h3 className="text-lg font-black text-slate-900">{t("sales.title")}</h3>
        </div>
        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm font-bold bg-white text-slate-900"
          data-testid="lager-sales-year"
        >
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">{t("sales.total_year")}</div>
          <div className="text-3xl font-black text-slate-900 tabular-nums mt-1" data-testid="lager-sales-total">{total}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">{t("sales.distinct")}</div>
          <div className="text-3xl font-black text-slate-900 tabular-nums mt-1">{topItems.length}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 col-span-2 sm:col-span-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">{t("sales.best_month")}</div>
          <div className="text-xl font-black text-slate-900 mt-1">
            {(() => { const b = [...chartData].sort((a, c) => c.sold - a.sold)[0]; return b && b.sold > 0 ? `${b.label} · ${b.sold}` : "—"; })()}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : total === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-500 italic" data-testid="lager-sales-empty">
          {t("sales.empty")}
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black mb-3">{t("sales.by_month")} · {year}</div>
            <div style={{ width: "100%", height: 280 }} data-testid="lager-sales-chart">
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Bar dataKey="sold" name={t("sales.sold")} fill="#0891b2" radius={[6, 6, 0, 0]} maxBarSize={44} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 text-[10px] uppercase tracking-widest text-slate-500 font-black">{t("sales.top_items")}</div>
            <div className="divide-y divide-slate-100">
              {topItems.slice(0, 25).map((it, i) => (
                <div key={`${it.tripletex_code}-${i}`} className="flex items-center gap-3 px-4 py-2.5" data-testid={`lager-sales-item-${i}`}>
                  <span className="w-6 text-xs font-black text-slate-400 tabular-nums">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-900 truncate">{it.name || "—"}</div>
                    {it.tripletex_code && <div className="text-[10px] text-slate-400 font-mono">Tripletex: {it.tripletex_code}</div>}
                  </div>
                  <span className="text-sm font-black text-cyan-700 tabular-nums">{it.sold}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function DoserneLager() {
  const { user, loading, authHeader } = useDoserneAuth();
  const t = useLagerT();
  const [tab, setTab] = useState("shop");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [systemsOrder, setSystemsOrder] = useState([]);
  const [cart, setCart] = useState([]);
  const [isSalg, setIsSalg] = useState(false);
  const [isDepot, setIsDepot] = useState(false);
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProjectState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("DOSERNE_LAGER_ACTIVE_PROJECT") || "null"); } catch { return null; }
  });
  const setActiveProject = useCallback((p) => {
    setActiveProjectState(p);
    try { localStorage.setItem("DOSERNE_LAGER_ACTIVE_PROJECT", JSON.stringify(p || null)); } catch { /* ignore */ }
  }, []);
  const [showCatalog, setShowCatalog] = useState(false);
  const [ready, setReady] = useState(false);
  const [reqRefresh, setReqRefresh] = useState(0);
  const [reqOpen, setReqOpen] = useState(0);
  const [urgentCount, setUrgentCount] = useState(0);
  const prevUrgentRef = useRef(null);
  const [activeCarts, setActiveCarts] = useState(0);

  const reload = useCallback(async () => {
    try {
      const base = [
        axios.get(`${API}/doserne/lager/catalog`, authHeader()),
        axios.get(`${API}/doserne/lager/cart`, authHeader()),
        axios.get(`${API}/doserne/lager/requests`, authHeader()),
      ];
      if (user?.is_admin) base.push(axios.get(`${API}/doserne/lager/active-carts`, authHeader()));
      const [cat, myCart, reqs, active] = await Promise.all(base);
      setItems(cat.data.items || []);
      setCategories(cat.data.categories || []);
      setSystemsOrder(cat.data.systems || []);
      setIsDepot(!!cat.data.is_depot);
      setCart(myCart.data.items || []);
      setIsSalg(!!myCart.data.is_salg);
      setReqOpen((reqs.data.requests || []).filter((r) => r.status !== "done" && r.status !== "cancelled").length);
      const uc = reqs.data.urgent_count || 0;
      const canSee = !!user?.is_admin || !!cat.data.is_depot;
      if (canSee && prevUrgentRef.current !== null && uc > prevUrgentRef.current) {
        toast(t("depot.alert", { n: uc }), { icon: "📦" });
      }
      if (canSee) prevUrgentRef.current = uc;
      setUrgentCount(canSee ? uc : 0);
      setActiveCarts(active ? (active.data.carts || []).length : 0);
    } catch { /* ignore */ }
    setReady(true);
  }, [authHeader, user]);

  const loadProjects = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/doserne/projects`, authHeader()); setProjects(data.projects || []); }
    catch { /* ignore */ }
  }, [authHeader]);

  useEffect(() => { if (user) { reload(); loadProjects(); } }, [user, reload, loadProjects]);

  const addToCart = async (payload) => {
    try {
      await axios.post(`${API}/doserne/lager/cart`, payload, authHeader());
      toast.success(t("toast.reserved"));
      await reload();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || t("toast.reserve_fail")); return false; }
  };
  const adjustStock = async (itemId, quantity) => {
    try {
      await axios.patch(`${API}/doserne/lager/catalog/${itemId}/stock`, { quantity }, authHeader());
      toast.success(t("toast.stock_ok"));
      await reload();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); return false; }
  };
  const setQty = async (line, qty) => {
    if (qty < 1) return;
    try { await axios.put(`${API}/doserne/lager/cart/${line.id}`, { quantity: qty }, authHeader()); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
  };
  const removeLine = async (line) => {
    try { await axios.delete(`${API}/doserne/lager/cart/${line.id}`, authHeader()); await reload(); }
    catch { toast.error(t("toast.err")); }
  };
  const convertCart = async (projectId = "", projectName = "", deliveryDate = "") => {
    try {
      const body = projectId ? { project_id: projectId, project_name: projectName } : {};
      if (deliveryDate) body.delivery_date = deliveryDate;
      const { data } = await axios.post(`${API}/doserne/lager/cart/convert`, body, authHeader());
      toast.success(t("toast.sent", { code: data.code }));
      await reload();
      setReqRefresh((k) => k + 1);
      setTab("requests");
    } catch (e) { toast.error(e?.response?.data?.detail || t("toast.err")); }
  };
  // "Add more" — preselect the project as the active list and jump to the shop.
  const addMoreToProject = (projectId = "", projectName = "") => {
    if (projectId) setActiveProject({ id: projectId, name: projectName || projectId });
    setTab("shop");
  };

  // SSE live — re-fetch stock/cart when anyone reserves/changes stock.
  useEffect(() => {
    if (!user) return undefined;
    const token = (authHeader().headers?.Authorization || "").replace("Bearer ", "");
    if (!token) return undefined;
    let es;
    try {
      es = new EventSource(`${API}/doserne/lager/events/stream?token=${encodeURIComponent(token)}`);
      es.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data?.type === "requests") setReqRefresh((k) => k + 1);
          reload();
        } catch { /* ping */ }
      };
      es.onerror = () => { /* browser auto-reconnects */ };
    } catch { /* no SSE */ }
    return () => { if (es) es.close(); };
  }, [user, authHeader, reload]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;

  const cartCount = cart.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
  const tabs = [
    { key: "shop", label: t("tab.shop"), Icon: Boxes },
    { key: "lowstock", label: t("tab.lowstock"), Icon: AlertTriangle },
    { key: "cart", label: t("tab.cart"), Icon: ShoppingCart },
    { key: "requests", label: t("tab.req"), Icon: ClipboardList },
    ...((user.is_admin || isDepot) ? [{ key: "depot", label: t("tab.depot"), Icon: PackageCheck }] : []),
    ...(user.is_admin ? [{ key: "active", label: t("tab.active"), Icon: Users }] : []),
    ...(user.is_admin ? [{ key: "sales", label: t("tab.sales"), Icon: TrendingUp }] : []),
    ...((user.is_admin || isDepot) ? [{ key: "history", label: t("tab.history"), Icon: History }] : []),
  ];

  return (
    <DoserneShell>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-black">{t("kicker")}</div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Lager</h1>
        </div>
        {user.is_admin && (
          <button onClick={() => setShowCatalog(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 text-sm font-semibold" data-testid="lager-catalog-btn">
            <Settings2 className="w-4 h-4" /> <span className="hidden sm:inline">{t("catalog")}</span>
          </button>
        )}
      </div>

      {ready && <OverviewBar items={items} reqOpen={reqOpen} activeCarts={activeCarts} isAdmin={!!user.is_admin} onGo={setTab} />}

      <div className="flex items-center gap-1 mb-5 border-b border-slate-200">
        {tabs.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-semibold border-b-2 transition ${tab === key ? "border-cyan-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            data-testid={`lager-tab-${key}`}
          >
            <Icon className="w-4 h-4" /> {label}
            {key === "cart" && cartCount > 0 && <span className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-cyan-600 text-white text-[10px] font-black flex items-center justify-center">{cartCount}</span>}
            {key === "depot" && urgentCount > 0 && <span className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center animate-pulse" data-testid="lager-depot-badge">{urgentCount}</span>}
          </button>
        ))}
      </div>

      {!ready ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          {tab === "shop" && <ShopTab items={items} categories={categories} systemsOrder={systemsOrder} projects={projects} onAdd={addToCart} isAdmin={!!user.is_admin} isDepot={isDepot} onAdjustStock={adjustStock} authHeader={authHeader} activeProject={activeProject} setActiveProject={setActiveProject} onReload={reload} />}
          {tab === "lowstock" && <LowStockTab items={items} />}
          {tab === "cart" && <CartTab cart={cart} isSalg={isSalg} onQty={setQty} onRemove={removeLine} onConvert={convertCart} onAddMore={addMoreToProject} activeProject={activeProject} setActiveProject={setActiveProject} projects={projects} />}
          {tab === "requests" && <RequestsTab authHeader={authHeader} isAdmin={!!user.is_admin} items={items} refreshKey={reqRefresh} onAddMore={addMoreToProject} />}
          {tab === "depot" && <DepotTab authHeader={authHeader} refreshKey={reqRefresh} />}
          {tab === "active" && user.is_admin && <ActiveCartsTab authHeader={authHeader} />}
          {tab === "sales" && user.is_admin && <SalesTab authHeader={authHeader} />}
          {tab === "history" && (user.is_admin || isDepot) && <HistoryTab authHeader={authHeader} />}
        </>
      )}

      {showCatalog && (
        <CatalogAdmin onClose={() => setShowCatalog(false)} authHeader={authHeader} onChanged={reload} />
      )}
    </DoserneShell>
  );
}
